import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../utils/supabase';
import {
  removePlayerFromCourt,
  removeCourt,
  updateMatchResult,
  adjustCourtCount,
  adjustWaitingCourtCount,
  padUpcomingQueueToSlotCount,
  buildPrematchEloSnapshots,
  courtNeedsPrematchEloSnapshot,
  courtRosterSig,
} from '../utils/courtUtils';
import {
  markCourtMatchStarted,
  restartCourtMatchTimer,
  normalizeMatchStartTime,
} from '../utils/matchTimerUtils';

import { updatePlayerElos } from '../services/sessionService';
import {
  claimSessionCourtScoreSubmission,
  abortSessionCourtScoreSubmission,
  ScoreSubmissionClaimError,
  updateSingleCourtAtomic,
} from '../services/sessionStateService';
import { finishMatchCmd, completeSessionScoreCmd, finishPegBoardMatchCmd } from '../services/sessionCommands';
import { createFinalEloHistoryRecords } from '../services/eloHistoryService';
import { isSessionMensDoublesEloExemptLineup } from '@badminton/matchmaking-core';
import { getBestCourtAssignments_TwoStage } from '../utils/matchmakingUtils';
import { detectPairType } from '../utils/pairTypeDetector';
import { detectGameType as detectGameTypeUtil } from '../utils/gameTypeUtils';
import { getSinglesEloForComputation } from '../utils/playerUtils';
import { isPartnership, getPartnerships, updatePartnerships } from '../services/partnershipService';
import { gameAffectsElo } from '../services/sessionService';
import React from 'react'; // Added for React.startTransition
import { MATCHMAKING_ALGORITHM_NAME, GAME_CREATION_ORIGIN, ensureGameCreationMeta } from '../constants/gameCreation';
import { fetchMatchHistoryForSession } from '../services/sessionMatchHistoryService';
import {
  getPlayersPerMatch,
  getPlayersPerSide,
  isSinglesFormat,
} from '../utils/competitiveMatchFormat';
import {
  isValidCourtRoster,
  isFullCourtRosterForSides,
  countPlayersOnCourt,
  deriveConsistentWaitingList,
  MATCH_FORMAT_DOUBLES,
  MATCH_FORMAT_SINGLES,
} from '../utils/sessionMatchModel';
import {
  buildUpcomingEligiblePool,
  buildUpcomingSessionState,
  planUpcomingSlot,
} from '@badminton/matchmaking-core';
import { collectScheduledGameIdsFromAssignmentRows } from '../utils/scheduledGameConsumption';
import { computeRefillIntent } from '../utils/computeRefillIntent';
import { createScoreSubmitTimer, scoreSubmitDebugLog } from '../utils/scoreSubmitDebug';
import { applyOptimisticPostScoreLayout } from '../services/applyPostScoreServerResult';
import {
  eloRecordsToServerPayload,
  persistScoreEloAsync,
  isGameEloPersisted,
} from '../services/scoreEloPipeline';
import {
  OFFLINE_COMMAND_TYPES,
  isBrowserOffline,
  shouldRunLocalHostPath,
} from '../utils/session/offlineQueueConstants';
import { buildLocalPostScoreResult } from '../utils/session/localPostScoreFill';
import { mergeLocalMatchHistory } from '../utils/session/buildLocalSessionGames';
import { applyLocalActiveCourtCount } from '../utils/session/applyLocalCourtCount';

function offlineQueueIsPending(offlineQueue) {
  if (!offlineQueue) return false;
  if (typeof offlineQueue.hasUnsyncedCommands === 'function') {
    return offlineQueue.hasUnsyncedCommands();
  }
  return Boolean(offlineQueue.hasPending);
}

/** Matches CompetitiveSessionPage MAX_HOST_UPCOMING_QUEUE_SLOTS — cap for host upcoming queue. */
const MAX_HOST_UPCOMING_QUEUE_SLOTS = 2;

/** Set true only when debugging court / matchmaking state flows. */
const COURT_MANAGEMENT_DEBUG_LOGS = false;
const dlog = (...args) => {
  if (COURT_MANAGEMENT_DEBUG_LOGS) console.log(...args);
};

/** Sorted id signature for team equality (order-independent). */
function teamIdsKey(teamIds) {
  return [...(teamIds || [])].filter(Boolean).map(String).sort().join('\u001f');
}

function teamsMatchAsSides(court, hist) {
  const g1 = hist?.teams?.[0] || [];
  const g2 = hist?.teams?.[1] || [];
  const direct =
    teamIdsKey(court.team1) === teamIdsKey(g1) && teamIdsKey(court.team2) === teamIdsKey(g2);
  const swapped =
    teamIdsKey(court.team1) === teamIdsKey(g2) && teamIdsKey(court.team2) === teamIdsKey(g1);
  if (direct) return 'direct';
  if (swapped) return 'swapped';
  return null;
}

function winnerOnCourtFromHistory(alignment, entry) {
  if (!entry || entry.result === 'draw') return null;
  if (alignment === 'direct') return entry.result;
  return entry.result === 'team1' ? 'team2' : 'team1';
}

function findLatestHistoryMatchForCourt(court, matchHistory) {
  if (!Array.isArray(matchHistory) || matchHistory.length === 0) return null;
  for (let i = matchHistory.length - 1; i >= 0; i--) {
    const entry = matchHistory[i];
    const alignment = teamsMatchAsSides(court, entry);
    if (!alignment) continue;
    const winner = winnerOnCourtFromHistory(alignment, entry);
    if (winner) {
      return { winner, score: entry.score };
    }
  }
  return null;
}

/**
 * DB + Realtime snapshots omit post-score UI fields (winner, score). After reload/sync the court
 * can still have full rosters with in_progress=false, which hides both score entry and Rotate.
 * Reattach winner/score from the latest session_games row that matches this lineup.
 */
function reconcileAwaitingRotationCourtsFromHistory(courtAssignments, matchHistory, playersPerSide) {
  if (!Array.isArray(courtAssignments) || courtAssignments.length === 0) {
    return { courts: courtAssignments, changed: false };
  }
  let changed = false;
  const courts = courtAssignments.map((court) => {
    const t1 = court.team1?.length || 0;
    const t2 = court.team2?.length || 0;
    const full = t1 === playersPerSide && t2 === playersPerSide;
    if (!full || court.winner || court.inProgress) {
      return court;
    }
    const found = findLatestHistoryMatchForCourt(court, matchHistory);
    if (!found) return court;
    changed = true;
    return {
      ...court,
      winner: found.winner,
      score: found.score,
      inProgress: false,
    };
  });
  return { courts, changed };
}

const resolveGameCreateMethodForCourt = (court) => {
  const meta = court?.gameCreation;
  if (!meta || !meta.origin) {
    return {
      method: 'authoritative',  // Changed from GAME_CREATION_ORIGIN.MANUAL to 'authoritative'
      algorithm: null,
    };
  }

  switch (meta.origin) {
    case GAME_CREATION_ORIGIN.MATCHMAKING:
      return {
        method: meta.isAltered ? 'matchmaking_altered' : 'matchmaking',
        algorithm: meta.algorithm || MATCHMAKING_ALGORITHM_NAME,
      };
    case GAME_CREATION_ORIGIN.SCHEDULED:
      return {
        method: meta.isAltered ? 'scheduled_altered' : 'scheduled',
        algorithm: null,
      };
    case GAME_CREATION_ORIGIN.MANUAL:
    default:
      return {
        method: 'authoritative',  // Changed from GAME_CREATION_ORIGIN.MANUAL to 'authoritative'
        algorithm: null,
      };
  }
};

const createMatchmakingMeta = ({
  origin,
  algorithm = MATCHMAKING_ALGORITHM_NAME,
  scheduledGameId = null,
  isAltered = false,
} = {}) => ({
  origin,
  algorithm,
  scheduledGameId,
  isAltered,
});

// Helper function to get game type ID from the new detectGameType function
const detectGameType = (team1Players, team2Players) => {
  const gameType = detectGameTypeUtil(team1Players, team2Players);
  return gameType.id; // Extract the ID from the object
};

/**
 * Custom hook for managing court assignments, waiting lists, and game flow.
 * Accepts a single options object so adding/removing options doesn't break callers.
 * @param {Object} options
 * @param {Array} [options.players]
 * @param {Array} [options.activePlayers]
 * @param {string} [options.sessionId]
 * @param {Function} [options.updatePlayers]
 * @param {Object} [options.playersById]
 * @param {Object} [options.sessionPlayerStatus]
 * @param {Function} [options.fetchPlayers]
 * @param {string|null} [options.seasonId]
 * @param {Array} [options.partnerships]
 * @param {boolean} [options.isSocialSession]
 * @param {string|null} [options.clubId]
 * @param {Function|null} [options.setPlayerSessionStatus]
 * @param {Array} [options.scheduledGames]
 * @param {string} [options.gameTypeRestriction]
 * @param {Object|null} [options.playerCategories]
 * @param {string} [options.matchFormat] 'doubles' | 'singles'
 * @param {boolean} [options.autoRotateAfterScoreSubmission] When true (default), after a successful finish_session_match the same rotation as "Rotate All Players" runs immediately (finished players → waitlist, next-on-court promoted).
 * @param {Function} [options.onAfterAutoRotateFromScore] Optional async hook (e.g. refresh games count + recalculate waiting list) invoked after auto-rotation from score submission.
 * @param {Function} [options.onAfterFinishMatchSync] When set (competitive normalized sessions), after finish_session_match succeeds the DB already applied waitlist + promotion — call this to reload courts/waitlist (e.g. handleRemoteSessionSync) instead of client rotateAndRefillCourts.
 * @param {Function} [options.onApplyOptimisticPostScore] Called after optimistic layout is applied locally (e.g. bump command epoch on page).
 * @param {Function} [options.onScoreStatsIncrement] Called after score commit with team ids and winningTeam for incremental games/streaks update.
 * @param {Function} [options.onScoreRecordingPreviewOpen] When set (e.g. host on competitive session), called at the start of score submission with the first upcoming slot snapshot so the host can show who is next on court.
 * @returns {Object} Court management state and functions
 */
/** Sort order matches generateMissingUpcomingCourts / DB next_up ordering. */
function firstUpcomingSlotFromState(nextAssignments) {
  const sorted = [...(nextAssignments || [])].sort(
    (a, b) => (Number(a.courtNumber) || 0) - (Number(b.courtNumber) || 0)
  );
  return sorted[0] || null;
}

/**
 * All player ids on active courts plus every upcoming row except `excludeSlotIndex`
 * (that row is treated as empty for refill so its players are not blocked from the pool).
 */
function occupiedIdsExceptUpcomingSlot(courtAssignments, workingSlots, excludeSlotIndex) {
  const s = new Set();
  for (const c of courtAssignments || []) {
    for (const id of [...(c?.team1 || []), ...(c?.team2 || [])]) {
      if (id) s.add(id);
    }
  }
  for (let idx = 0; idx < (workingSlots || []).length; idx++) {
    if (idx === excludeSlotIndex) continue;
    const row = workingSlots[idx];
    for (const id of [...(row?.team1 || []), ...(row?.team2 || [])]) {
      if (id) s.add(id);
    }
  }
  return s;
}

async function submitLocalOfflineScore({
  offlineQueue,
  getOfflineLiveState,
  court,
  courtNum,
  scorePayload,
  pendingEloHistoryRecords,
}) {
  const extras = typeof getOfflineLiveState === 'function' ? getOfflineLiveState() : {};
  const liveState = {
    ...extras,
    courtAssignments: extras.courtAssignments,
    nextAssignments: extras.nextAssignments,
    waitingList: extras.waitingList,
    playersById: extras.playersById,
    sessionPlayerStatus: extras.sessionPlayerStatus,
    playerWaitMetrics: extras.playerWaitMetrics,
    matchHistory: extras.matchHistory,
    partnerships: extras.partnerships,
    playerInteractions: extras.playerInteractions,
    scheduledGames: extras.scheduledGames,
    session: extras.session,
  };
  const local = buildLocalPostScoreResult(liveState, {
    courtNumber: courtNum,
    team1: court.team1,
    team2: court.team2,
    scoreEventId: scorePayload.scoreEventId,
  });
  const finishing = [...(court.team1 || []), ...(court.team2 || [])];
  const snapshot = {
    ...liveState,
    courtAssignments: local.courtAssignments,
    nextAssignments: local.nextAssignments,
    waitingList: local.waitingList,
    matchHistory: [
      ...(liveState.matchHistory || []),
      {
        gameId: `local:${scorePayload.scoreEventId}`,
        team1: court.team1,
        team2: court.team2,
        teams: [court.team1, court.team2],
        players: finishing,
      },
    ],
    totalGamesCompleted: (liveState.totalGamesCompleted || 0) + 1,
    scheduledGames: local.scheduledGames,
  };
  await offlineQueue.enqueue({
    type: OFFLINE_COMMAND_TYPES.SCORE,
    ...scorePayload,
    matchmakingAlgorithm: scorePayload.matchmakingAlgorithm || 'two_stage_local',
  }, snapshot);
  for (const seat of local.seatCommands) {
    await offlineQueue.enqueue(seat, snapshot);
  }
  const eloDeltas = {};
  (pendingEloHistoryRecords || []).forEach((record) => {
    if (!record?.player_id) return;
    eloDeltas[record.player_id] = (eloDeltas[record.player_id] || 0) + (Number(record.rating_change) || 0);
  });
  return {
    ok: true,
    local: true,
    gameId: `local:${scorePayload.scoreEventId}`,
    courtVersion: scorePayload.expectedCourtVersion ?? court.version ?? 0,
    promoted: local.promoted,
    promotedFromSlot: local.promotedFromSlot,
    fills: local.fills,
    courtAssignments: snapshot.courtAssignments,
    nextAssignments: snapshot.nextAssignments,
    waitingList: snapshot.waitingList,
    scheduledGames: local.scheduledGames,
    completedScheduledGameIds: local.completedScheduledGameIds,
    placedScheduledGameIds: local.placedScheduledGameIds,
    eloDeltas,
  };
}

const useCourtManagement = ({
  players: playersArg,
  activePlayers: activePlayersArg,
  sessionId,
  updatePlayers,
  playersById = {},
  sessionPlayerStatus = {},
  fetchPlayers,
  seasonId = null,
  partnerships = [],
  isSocialSession = false,
  isPegBoardSession = false,
  clubId = null,
  setPlayerSessionStatus = null,
  scheduledGames: scheduledGamesArg = [],
  sessionPlayerScheduledGames = {},
  /** When a scheduled matchup lands on a playing court via rotation, update its status to 'placed'. */
  onConsumeScheduledGames = null,
  /** Offline score: drop completed scheduled games and mark promoted ones placed. */
  onScheduledGamesChange = null,
  gameTypeRestriction = 'all_types',
  playerCategories = null,
  matchFormat = 'doubles',
  autoRotateAfterScoreSubmission = true,
  onAfterAutoRotateFromScore = null,
  onAfterFinishMatchSync = null,
  onApplyOptimisticPostScore = null,
  /**
   * Phase B: called with the SCORE_COMPLETED result from the EF response when available.
   * Replaces the optimistic layout apply on the initiator tab with authoritative DB values.
   */
  onApplyScoreCommandResult = null,
  onScoreStatsIncrement = null,
  onScoreRecordingPreviewOpen = null,
  /** Competitive: playing court number after score + rotation/sync so UI can pulse the card border */
  onCourtFreshRotationHighlight = null,
  /** When false, match preferences are ignored for matchmaking (session setting). Default true. */
  matchPreferencesEnabled = true,
  /** When false, cooldown is penalty-only (no pool/scoring hard blocks). Default true. */
  cooldownEnabled = false,
  /** Per-tab id passed to RPCs for command idempotency (writer lease retired). */
  tabInstanceId = null,
  /**
   * Acquires the score-prep quiescence lock: blocks new next_up debounced writes and defers
   * realtime next_up merges until the score RPC completes.  Resolves once the queue is
   * quiescent (or after a 3 s timeout).  Provided by CompetitiveSessionPage.
   */
  acquireScorePrepLock = null,
  /** Releases the score-prep lock set by acquireScorePrepLock.  Must be called in finally. */
  releaseScorePrepLock = null,
  /** Called when async ELO persistence fails after score was saved. */
  onScoreEloError = null,
  /** Host Offline-mode command log. Live scores still wait for the server. */
  offlineQueue = null,
  /** Extra live session fields (interactions, session rules) for local matchmaking. */
  getOfflineLiveState = null,
} = {}) => {
  const playersPerSide = useMemo(() => getPlayersPerSide(matchFormat), [matchFormat]);
  const playersPerMatch = useMemo(() => getPlayersPerMatch(matchFormat), [matchFormat]);
  let players = playersArg || [];
  let activePlayers = activePlayersArg || [];
  let scheduledGames = Array.isArray(scheduledGamesArg) ? scheduledGamesArg : [];
  // Defensive: Only use player IDs that are present in playersById and active
  const validActivePlayerIds = activePlayers
    .map(p => p.id)
    .filter(id => playersById[id] && sessionPlayerStatus[id] === 'active');

  // Wait metrics state for tracking court rotations and fairness
  const [playerWaitMetrics, setPlayerWaitMetrics] = useState({});
  
  // Ref to track latest wait metrics for matchmaking (avoids stale closure issues)
  // This ensures matchmaking always gets the most recent cooldown data, even before React re-renders
  const playerWaitMetricsRef = useRef({});
  
  // Keep ref in sync with state
  useEffect(() => {
    playerWaitMetricsRef.current = playerWaitMetrics;
  }, [playerWaitMetrics]);
  
  // Track processed game completions to prevent duplicate processing in StrictMode
  // This ref exists to handle double-calls from React.StrictMode and race conditions from async state updates
  const processedGameCompletions = useRef(new Set());
  // Per-court in-flight set: prevents concurrent score submissions for the same court index
  // (e.g. slow network + user double-click, or race between two tabs)
  const inFlightScoreSubmissions = useRef(new Set());

  /**
   * Active court numbers where finish_session_match has released session_players on the
   * server (empty slot) while local React state still holds team1/team2 for rotate UX.
   * Debounced persistence must skip updateSingleCourtAtomic for these courts until
   * rotateAndRefillCourts applies the next lineup — otherwise we would re-assign the
   * finished players back onto the court in the DB.
   */
  const serverClearedActiveCourtsRef = useRef(new Set());
  /** Roster sig captured right after finish_session_match; if host edits before rotate, debounce may persist. */
  const serverClearedCourtRosterSigRef = useRef({});

  /**
   * Helper function to call the correct matchmaking algorithm based on mode
   * @param {Array} playerIds - Available player IDs
   * @param {number} numCourts - Number of courts to fill
   * @param {Object} sessionState - Session state for matchmaking
   * @param {Object} playersById - Map of player IDs to player objects
   * @param {Array} partnerships - Active partnerships
   * @returns {Object} Matchmaking result with courts array
   */
  const callMatchmaking = useCallback((playerIds, numCourts, sessionState, playersById, partnerships, scheduledGames = [], gameTypeRestriction = 'all_types', categories = null) => {
    const latestMetrics = playerWaitMetricsRef.current || {};
    const enrichedSessionState = {
      ...sessionState,
      matchPreferencesEnabled: matchPreferencesEnabled !== false,
      cooldownEnabled: cooldownEnabled === true,
    };
    return getBestCourtAssignments_TwoStage(
      playerIds,
      numCourts,
      enrichedSessionState,
      playersById,
      false,
      partnerships,
      latestMetrics,
      scheduledGames,
      gameTypeRestriction,
      categories,
      matchFormat
    );
  }, [matchFormat, matchPreferencesEnabled, cooldownEnabled]);
  // State management
  // Change courtAssignments and waitingList to use player IDs
  const [courtAssignments, setCourtAssignments] = useState([]); // [{ team1: [id1, id2], team2: [id3, id4] }]
  const [waitingList, setWaitingList] = useState([]); // [id1, id2, ...] - only active players
  const [pausedList, setPausedList] = useState([]); // [id1, id2, ...] - only paused players
  const [numberOfCourtsState, setNumberOfCourtsState] = useState(2); // Default 2 courts for competitive sessions
  const [userSetNumberOfCourts, setUserSetNumberOfCourts] = useState(false);
  const [numberOfWaitingCourts, setNumberOfWaitingCourts] = useState(1);
  const [numberOfUpcomingCourts, setNumberOfUpcomingCourts] = useState(1);
  const [currentClubId, setCurrentClubId] = useState(null);
  const [clubStrengths, setClubStrengths] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [courtsAssigned, setCourtsAssigned] = useState(false);
  const [nextAssignments, setNextAssignments] = useState([]);
  
  // --- Game History State ---
  // Normalized array of completed games
  // Each game: { id, courtId, team1, team2, team1Score, team2Score, timestamp, ... }
  const [gameHistory, setGameHistory] = useState([]);

  // Freeze doubles prematch Elo + discipline games when a court is full and in progress.
  useEffect(() => {
    if (isSocialSession) return;
    setCourtAssignments((prev) => {
      let changed = false;
      const next = prev.map((court) => {
        if (!courtNeedsPrematchEloSnapshot(court, playersById)) return court;
        const t1 = court.team1 || [];
        const t2 = court.team2 || [];
        const snaps = buildPrematchEloSnapshots(t1, t2, playersById);
        if (Object.keys(snaps).length !== 4) return court;
        changed = true;
        return { ...court, eloSnapshots: snaps };
      });
      return changed ? next : prev;
    });
  }, [courtAssignments, playersById, isSocialSession]);

  // Utility to add a completed game to gameHistory (pure function)
  const addGameToHistory = (game) => {
    setGameHistory(prev => {
      // Check if this game is already in history (prevent duplicate entries)
      const gameExists = prev.some(existingGame => 
        existingGame.courtId === game.courtId && 
        existingGame.timestamp === game.timestamp &&
        existingGame.team1Score === game.team1Score &&
        existingGame.team2Score === game.team2Score
      );
      
      if (gameExists) {
        dlog('Game already exists in history, skipping duplicate entry');
        return prev;
      }
      
      const updated = [
        ...prev,
        { ...game, id: `game_${Date.now()}_${Math.random().toString(36).slice(2,8)}` }
      ];
      return updated;
    });
  };


  
  // Ref to track if this is the initial load
  const isInitialLoad = useRef(true);

  // ===== CLUB MANAGEMENT =====

  /**
   * Fetches club strengths from Supabase
   */
  const fetchClubStrengths = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: supabaseError } = await supabase
        .from('clubs')
        .select('id, strength_rating');
      if (supabaseError) throw supabaseError;
      
      const strengths = {};
      data.forEach(club => {
        strengths[club.id] = club.strength_rating || 1500;
      });
      strengths.default = 1500;
      
      setClubStrengths(strengths);
      
      // Only set currentClubId on initial load if it's not already set
      if (isInitialLoad.current && !currentClubId) {
        if (clubId) {
          // Use the club ID passed from Dashboard (session's club)
          setCurrentClubId(clubId);
          dlog('🏢 useCourtManagement: Setting currentClubId from session club:', clubId);
        } else if (data.length > 0) {
          // Fallback to first club if no clubId provided
        setCurrentClubId(data[0].id);
          dlog('🏢 useCourtManagement: Setting currentClubId to first club (fallback):', data[0].id);
        }
        isInitialLoad.current = false;
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch club strengths');
    } finally {
      setLoading(false);
    }
  }, []); // Remove currentClubId from dependencies

  /**
   * Sets the current club for court management
   * @param {string} clubId - Club ID to set as current
   */
  const setClub = useCallback(async (clubId) => {
    setCurrentClubId(clubId);
  }, []);

  // ===== COURT OPERATIONS =====

  // Match history for matchmaking
  const [matchHistory, setMatchHistory] = useState([]); // [{players: [id...], teams: [[id...],[id...]], result, timestamp}]

  const reloadMatchHistory = useCallback(async () => {
    if (!sessionId || isSocialSession) {
      return null;
    }
    try {
      const history = await fetchMatchHistoryForSession(sessionId);
      setMatchHistory((prev) => mergeLocalMatchHistory(history, prev));
      return history;
    } catch (err) {
      console.error('❌ Failed to load match history:', err);
      return null;
    }
  }, [sessionId, isSocialSession]);

  // Load match history from DB so interactions persist across refreshes
  useEffect(() => {
    if (!sessionId || isSocialSession) {
      return;
    }
    reloadMatchHistory();
  }, [sessionId, isSocialSession, reloadMatchHistory]);

  // Rehydrate winner/score on courts after DB reload/sync (normalized tables omit those fields).
  useEffect(() => {
    if (!sessionId || isSocialSession || !matchHistory?.length) {
      return;
    }
    setCourtAssignments((prev) => {
      if (!prev?.length) return prev;
      const { courts, changed } = reconcileAwaitingRotationCourtsFromHistory(
        prev,
        matchHistory,
        playersPerSide
      );
      return changed ? courts : prev;
    });
  }, [sessionId, isSocialSession, matchHistory, playersPerSide]);

  // Helper to get all player IDs currently on court
  const getPlayersOnCourts = (courts) => new Set(courts.flatMap(c => [...c.team1, ...c.team2]));

  // Helper to get the latest player object by id from courts, upcoming matches, or fallback to activePlayers
  function getLatestPlayerById(playerId, courts, nextAssignments, activePlayers, updatedPlayersFromMatch = []) {
    // Search courts (most up-to-date, contains players who just finished matches)
    for (const court of courts) {
      for (const p of [...court.team1, ...court.team2]) {
        if (p.id === playerId) return p;
      }
    }
    // Search updated players from the most recent match (if provided)
    const updatedPlayer = updatedPlayersFromMatch.find(p => p.id === playerId);
    if (updatedPlayer) return updatedPlayer;
    // Search upcoming matches
    for (const match of nextAssignments) {
      for (const p of [...match.team1, ...match.team2]) {
        if (p.id === playerId) return p;
      }
    }
    // Search global players array (contains most up-to-date ELOs after matches)
    const globalPlayer = players.find(p => p.id === playerId);
    if (globalPlayer) return globalPlayer;
    // Fallback to activePlayers
    return activePlayers.find(p => p.id === playerId);
  }

  // Debug logging helper
  const logPlayerLevels = (context, courts, next, waiting) => {
    dlog(`=== ${context} ===`);
    dlog('Active Courts:', courts.map(c => ({
      courtNumber: c.courtNumber,
      inProgress: c.inProgress,
      players: [...c.team1, ...c.team2].map(p => p.name)
    })));
    dlog('Upcoming Match:', next.length > 0 ? {
      players: [...next[0].team1, ...next[0].team2].map(p => p.name)
    } : 'None');
    dlog('Waiting Pool:', waiting.map(p => p.name));
    dlog('Waiting Pool Count:', waiting.length);
    dlog('================');
  };

  /**
   * Helper function to build sessionState consistently across all matchmaking calls
   * Ensures matchHistory is always included for normalized scoring system
   * @param {Object} params - Parameters for building sessionState
   * @param {Object} params.gamesPlayedThisSession - Map of player ID to games played count
   * @param {Array} params.courtAssignments - Current court assignments
   * @param {Set} [params.recentTeams] - Set of recent team combinations (optional)
   * @param {Set} [params.recentMatchups] - Set of recent matchup combinations (optional)
   * @param {Map} [params.playerInteractions] - Map of player interactions (optional)
   * @param {Object} [params.playerStreaks] - Object of player streaks (optional)
   * @returns {Object} Complete sessionState object for matchmaking
   */
  const buildSessionState = useCallback(({
    gamesPlayedThisSession,
    courtAssignments,
    recentTeams = new Set(),
    recentMatchups = new Set(),
    playerInteractions = new Map(),
    playerStreaks = {}
  }) => {
    return {
      gamesPlayedThisSession,
      recentTeams,
      recentMatchups,
      currentlyOnCourt: new Set(courtAssignments.flatMap(c => [...(c.team1 || []), ...(c.team2 || [])])),
      matchHistory: Array.isArray(matchHistory) ? matchHistory : [], // Always include for normalized scoring
      totalGamesCompleted: matchHistory?.length || 0,
      playerInteractions,
      playerStreaks,
      playersById // Include for matchmaking functions that need it
    };
  }, [matchHistory, playersById]);

  // Assign courts and set up the first upcoming match (legacy JSONB sessions only — not used when normalized + sessionId)
  const assignCourtsAndUpcoming = useCallback((customPartnerships = null) => {
    // For Social Sessions, don't auto-assign courts
    if (isSocialSession) {
      dlog('Social Session detected - skipping auto-assignment');
      setCourtsAssigned(true);
      setError(null);
      return { success: true, message: 'Social Session - manual court assignment only' };
    }

    // Use provided partnerships or the ones from props
    const effectivePartnerships = customPartnerships || partnerships;
    
    // Assign as many full courts as possible
    const gamesPlayedThisSession = {};
    activePlayers.forEach(p => {
      gamesPlayedThisSession[p.id] = p.gamesPlayedThisSession || 0;
    });
    const sessionState = buildSessionState({
      gamesPlayedThisSession,
      courtAssignments: []
    });
    // Assign all available players to active courts first
    const maxFullCourts = Math.floor(validActivePlayerIds.length / playersPerMatch);
    const courtsToAssign = Math.min(numberOfCourtsState, maxFullCourts);
    const result = callMatchmaking(
      validActivePlayerIds,
      courtsToAssign,
      sessionState,
      playersById,
      effectivePartnerships,
      [],
      gameTypeRestriction,
      playerCategories
    );
    const assignedCourts = result.courts.map((court, idx) => {
      const isFull =
        court.team1.length === playersPerSide && court.team2.length === playersPerSide;
      const assignedCourt = {
        courtNumber: idx + 1,
        team1: court.team1,
        team2: court.team2,
        inProgress: isFull, // Only active when both teams are full
        winner: null,
        score: null,
        startTime: null,
        // Store original team composition for ELO calculations
        originalTeam1: [...court.team1],
        originalTeam2: [...court.team2],
        gameCreation: createMatchmakingMeta({
          origin: GAME_CREATION_ORIGIN.MATCHMAKING,
          algorithm: MATCHMAKING_ALGORITHM_NAME,
          isAltered: false,
        })
      };
      if (isFull) {
        markCourtMatchStarted(assignedCourt);
      }
      return assignedCourt;
    });
    // Remove assigned players from pool
    const assignedIds = new Set(assignedCourts.flatMap(c => [...c.team1, ...c.team2]));
    const remainingPlayers = validActivePlayerIds.filter(id => !assignedIds.has(id));
    // Log which IDs are considered for upcoming matches
    dlog('[assignCourtsAndUpcoming] IDs considered for nextAssignments (upcoming matches):', remainingPlayers);
    // Set up multiple upcoming matches (if possible)
    let nextMatches = [];
    if (remainingPlayers.length >= playersPerMatch) {
      const maxUpcomingMatches = Math.min(
        numberOfUpcomingCourts,
        Math.floor(remainingPlayers.length / playersPerMatch)
      );
      if (maxUpcomingMatches > 0) {
        const nextResult = callMatchmaking(
          remainingPlayers,
          maxUpcomingMatches,
          sessionState,
          playersById,
          effectivePartnerships,
          [],
          gameTypeRestriction,
          playerCategories
        );
        nextMatches = nextResult.courts;
      }
    }
    // Defensive: Only include valid, active player IDs in waiting list
    const nextIds = nextMatches.flatMap(match => [...match.team1, ...match.team2]);
    const waitingPlayers = remainingPlayers.filter(id => !nextIds.includes(id) && playersById[id] && sessionPlayerStatus[id] === 'active');
    if (process.env.NODE_ENV === 'development') {
      const fmt = isSinglesFormat(matchFormat) ? MATCH_FORMAT_SINGLES : MATCH_FORMAT_DOUBLES;
      assignedCourts.forEach((c, i) => {
        if (!isValidCourtRoster(c, fmt)) {
          console.warn('[useCourtManagement] Roster invalid for format after matchmaking', {
            courtIndex: i,
            format: fmt,
          });
        }
      });
    }
    // Batch state updates
    React.startTransition(() => {
      setCourtAssignments(assignedCourts);
      setNextAssignments(nextMatches.map((court, i) => ({
        courtNumber: i,
        team1: court.team1,
        team2: court.team2,
        inProgress: false, // Keep upcoming matches as not in progress
        winner: null,
        score: null,
        gameCreation: createMatchmakingMeta({
          origin: GAME_CREATION_ORIGIN.MATCHMAKING,
          algorithm: MATCHMAKING_ALGORITHM_NAME,
          isAltered: false,
        }),
      })));
      setWaitingList(waitingPlayers);
    });
    setCourtsAssigned(true);
    setError(null);
    return result;
  }, [
    validActivePlayerIds,
    numberOfCourtsState,
    numberOfUpcomingCourts,
    matchHistory,
    playersById,
    sessionPlayerStatus,
    partnerships,
    isSocialSession,
    gameTypeRestriction,
    playerCategories,
    playersPerMatch,
    playersPerSide,
    callMatchmaking,
  ]);

  const assignCourts = assignCourtsAndUpcoming;

  // NEW ROTATION FLOW: Just rotate players, DO NOT auto-generate next-on-court
  // Optional snapshot: pass explicit courtAssignments / nextAssignments / waitingList / numberOfUpcomingCourts
  // (e.g. immediately after score submission before React has re-rendered).
  const rotateAndRefillCourts = useCallback((snapshot = null) => {
    dlog('🔄 [ROTATION] Starting rotation - will NOT auto-generate next-on-court');

    const baseCourts = snapshot?.courtAssignments ?? courtAssignments;
    const baseNext = snapshot?.nextAssignments ?? nextAssignments;
    const baseWaiting = snapshot?.waitingList ?? waitingList;
    const upcomingN = snapshot?.numberOfUpcomingCourts ?? numberOfUpcomingCourts;

    // Find finished courts (inProgress === false)
    const finishedCourts = baseCourts.map((court, idx) => ({ ...court, idx })).filter(c => !c.inProgress);

    if (finishedCourts.length === 0) {
      dlog('🔄 [ROTATION] No finished courts to rotate');
      return [];
    }

    let updatedCourts = [...baseCourts];
    let finishedPlayerIds = [];
    let usedUpcomingMatches = 0;
    /** scheduled_games IDs promoted from upcoming → playing court (status → on_court via callback). */
    const promotedScheduledGameIds = [];

    finishedCourts.forEach(finished => {
      // Collect finished players to add to waitlist
      finishedPlayerIds.push(...finished.team1, ...finished.team2);

      // Check if there's a next-on-court match for this court
      if (usedUpcomingMatches < baseNext.length) {
        const nextMatch = baseNext[usedUpcomingMatches];

        promotedScheduledGameIds.push(
          ...collectScheduledGameIdsFromAssignmentRows([nextMatch]),
        );

        // Promote next-on-court to playing court
        const promotedCourt = {
          ...nextMatch,
          courtNumber: finished.courtNumber,
          winner: null,
          score: null,
          originalTeam1: [...nextMatch.team1],
          originalTeam2: [...nextMatch.team2],
          gameCreation: createMatchmakingMeta({
            origin: nextMatch.gameCreation?.origin || GAME_CREATION_ORIGIN.MATCHMAKING,
            algorithm: nextMatch.gameCreation?.algorithm || MATCHMAKING_ALGORITHM_NAME,
            scheduledGameId: nextMatch.gameCreation?.scheduledGameId || null,
            isAltered: !!nextMatch.gameCreation?.isAltered,
          })
        };
        restartCourtMatchTimer(promotedCourt);
        updatedCourts[finished.idx] = promotedCourt;
        usedUpcomingMatches++;
      } else {
        // No next-on-court available - clear the court
        updatedCourts[finished.idx] = {
          courtNumber: finished.courtNumber,
          team1: [],
          team2: [],
          inProgress: false,
          winner: null,
          score: null,
          gameCreation: null,
        };
      }
    });

    // Add finished players to waitlist
    const newWaitingList = [...baseWaiting, ...finishedPlayerIds];

    // Keep any upcoming matches that were not promoted (e.g. "next on court 2" becomes the new first).
    // Pad to numberOfUpcomingCourts so empty slots stay visible (no second card unmount / jutter).
    const remainingNextAssignments = baseNext.slice(usedUpcomingMatches);
    const paddedNextAssignments = padUpcomingQueueToSlotCount(
      remainingNextAssignments,
      upcomingN,
      (idx) => ({
        courtNumber: idx,
        team1: [],
        team2: [],
        inProgress: false,
        winner: null,
        score: null,
        gameCreation: createMatchmakingMeta({
          origin: GAME_CREATION_ORIGIN.MANUAL,
          algorithm: null,
          scheduledGameId: null,
          isAltered: false,
        }),
      })
    );

    dlog('🔄 [ROTATION] Rotation complete:');
    dlog('  - Finished players moved to waitlist:', finishedPlayerIds.length);
    dlog('  - Next-on-court promoted to courts:', usedUpcomingMatches);
    dlog('  - Remaining upcoming queue length (before pad):', remainingNextAssignments.length);
    dlog('  - Padded upcoming queue length:', paddedNextAssignments.length);
    dlog('  - New waitlist size:', newWaitingList.length);
    
    setCourtAssignments(updatedCourts);
    setNextAssignments(paddedNextAssignments);
    setWaitingList(newWaitingList);

    finishedCourts.forEach((f) => {
      serverClearedActiveCourtsRef.current.delete(f.courtNumber);
      delete serverClearedCourtRosterSigRef.current[f.courtNumber];
    });

    const uniqPromotedScheduled = [...new Set(promotedScheduledGameIds)].filter(Boolean);
    if (uniqPromotedScheduled.length && typeof onConsumeScheduledGames === 'function') {
      void Promise.resolve(onConsumeScheduledGames(uniqPromotedScheduled)).catch((err) => {
        console.error('[useCourtManagement] Scheduled game consume after rotate failed:', err);
      });
    }

    // CRITICAL: Immediately update cooldown in database for finished players
    // This ensures cooldown shows instantly without waiting for debounced save
    if (finishedPlayerIds && finishedPlayerIds.length > 0) {
      dlog('🔥 COOLDOWN: Setting cooldown for finished players:', finishedPlayerIds);
      
        const currentTime = Date.now();
      const newMetrics = {};
        
        finishedPlayerIds.forEach(playerId => {
          newMetrics[playerId] = {
            courtsObserved: 0,
          isOnCooldown: true, // 🔥 CRITICAL: Set cooldown flag
            lastCourtUpdate: currentTime,
          lastGameCompletedAt: currentTime,
            consecutiveWaits: 0
          };
        });
        
      // Update local state for UI AND ref for matchmaking
      setPlayerWaitMetrics(prevMetrics => {
        const newState = {
          ...prevMetrics,
          ...newMetrics
        };
        // ⚡ CRITICAL: Update ref immediately so matchmaking gets latest data
        // This ensures cooldown is applied to matchmaking even before React re-renders
        playerWaitMetricsRef.current = newState;
        return newState;
      });
      
      dlog('🔥 COOLDOWN: Sample updated metric:', newMetrics[finishedPlayerIds[0]]);
      
      // ⚡ IMMEDIATE DATABASE UPDATE - Don't wait for debounced save!
      // This is the key fix - write cooldown to DB immediately
      // Only do this if we have a valid sessionId (not in test mode)
      if (sessionId) {
        import('../services/sessionStateService').then(SessionStateService => {
          return SessionStateService.resetCourtsWatched(sessionId, finishedPlayerIds);
        }).then(() => {
          dlog('✅ COOLDOWN: Immediately saved to database');
          
          // 🔄 RELOAD from DB to update UI immediately
          // This ensures the cooldown icon shows without refresh
          return import('../services/sessionStateService').then(SessionStateService => {
            return SessionStateService.loadNormalizedState(sessionId);
          });
        }).then(state => {
          if (state && state.playerWaitMetrics) {
            dlog('✅ COOLDOWN: Reloaded wait metrics from database');
            // Update both state and ref to keep them in sync
            playerWaitMetricsRef.current = state.playerWaitMetrics;
            setPlayerWaitMetrics(state.playerWaitMetrics);
          }
        }).catch(error => {
          console.error('❌ COOLDOWN: Failed to save/reload:', error);
    });
      }
    }
    
    // Return the finished player IDs for Dashboard tracking
    return finishedPlayerIds;
  }, [courtAssignments, nextAssignments, waitingList, numberOfUpcomingCourts, scheduledGames, onConsumeScheduledGames]);

  // REMOVED: Generate next on court preview useEffect - was causing unnecessary re-renders
  // This useEffect was empty but still running on every courtAssignments/players change


  /**
   * Updates match result for a specific court and saves to database
   * @param {number} courtIndex - Index of the court to update
   * @param {Object} score - Match score object with team1Score, team2Score, and winningTeam
   * @param {boolean} testMode - If true, disable fallbacks to test Edge Functions directly
   */
  const updateMatchResultHandler = useCallback(async (courtIndex, score, testMode = false) => {
    if (inFlightScoreSubmissions.current.has(courtIndex)) {
      scoreSubmitDebugLog('blocked', { reason: 'duplicate_in_flight', courtIndex });
      console.warn('[useCourtManagement] Score submission already in flight for court', courtIndex, '— ignoring duplicate call');
      return { needsGamesPlayedRefresh: false };
    }
    inFlightScoreSubmissions.current.add(courtIndex);
    /** Set when claim_session_court_score_submission succeeded (multi-tab score dedupe). */
    let scoreClaimActive = false;
    let claimCourtNumberForRpc = null;
    const courtForTimer = courtAssignments[courtIndex];
    const timerCourtNum = courtForTimer?.courtNumber ?? (courtIndex + 1);
    const scoreTimer = createScoreSubmitTimer(timerCourtNum);
    scoreSubmitDebugLog('start', { courtIndex, courtNumber: timerCourtNum, testMode });
    try {
    // Competitive normalized sessions always use the server-authoritative EF path
    // (completeSessionScoreCmd), which runs prepare + matchmaking + commit atomically.
    // Legacy finishMatchCmd is retained only for JSONB / social sessions.
    const isCompetitiveNormalized =
      !isSocialSession && !isPegBoardSession && typeof onAfterFinishMatchSync === 'function';
    const useServerMatchmaking =
      !isPegBoardSession &&
      (isCompetitiveNormalized || process.env.REACT_APP_SERVER_MATCHMAKING === 'true');
    // Acquire score-prep quiescence lock: blocks next_up debounced writes and realtime merges
    // until the score RPC completes, ensuring DB===UI for promotion reads.
    await acquireScorePrepLock?.();
    scoreTimer.mark('quiescence');
      /** Set after finish_session_match returns ok — optional auto-rotation runs at end of handler. */
      let shouldAutoRotateAfterScore = false;
      /** When true, finish RPC updated waitlist/promotion in DB; reload snapshot instead of client rotation. */
      let willSyncFromDbAfterFinish = false;
      /** False after paths that already call fetchGamesPlayedCount (full sync or afterAutoRotate). */
      let needsGamesPlayedRefresh = true;
      /** Server or legacy finish RPC result — used for post-score DB sync (fills debug). */
      let matchResult = null;

      const courtForClaim = courtAssignments[courtIndex];
      if (
        typeof onScoreRecordingPreviewOpen === 'function' &&
        !isSocialSession &&
        sessionId &&
        currentClubId &&
        courtForClaim
      ) {
        const previewCourtNum = courtForClaim.courtNumber ?? (courtIndex + 1);
        const firstUp = firstUpcomingSlotFromState(nextAssignments);
        const t1 = [...(firstUp?.team1 || [])].filter(Boolean);
        const t2 = [...(firstUp?.team2 || [])].filter(Boolean);
        const team1Names = t1.map((id) => playersById[id]?.name).filter(Boolean);
        const team2Names = t2.map((id) => playersById[id]?.name).filter(Boolean);
        try {
          onScoreRecordingPreviewOpen({
            activeCourtNumber: previewCourtNum,
            team1Ids: t1,
            team2Ids: t2,
            team1Names,
            team2Names,
            isComplete: t1.length === playersPerSide && t2.length === playersPerSide,
            hasAnyPlayers: t1.length + t2.length > 0,
          });
        } catch (e) {
          console.error('[useCourtManagement] onScoreRecordingPreviewOpen failed:', e);
        }
      }

      // Serialize score submission across devices (legacy finishMatchCmd path only).
      // Server-matchmaking acquires claim inline in prepare_score_transition via complete-session-score EF.
      if (!useServerMatchmaking && sessionId && currentClubId && courtForClaim) {
        claimCourtNumberForRpc = courtForClaim.courtNumber ?? (courtIndex + 1);
        const claim = await claimSessionCourtScoreSubmission(
          sessionId,
          claimCourtNumberForRpc,
          courtForClaim.version ?? null
        );
        if (claim?.success && claim.skipped !== true) {
          scoreClaimActive = true;
        }
      } else if (useServerMatchmaking && sessionId && currentClubId && courtForClaim) {
        claimCourtNumberForRpc = courtForClaim.courtNumber ?? (courtIndex + 1);
      }
      if (!useServerMatchmaking) {
        scoreTimer.mark('claim');
      }
      
      // 1. Capture pre-game ELOs before result submission
      const preGameElos = {};
      if (courtAssignments[courtIndex]) {
        const court = courtAssignments[courtIndex];
        
        // Detect game type for ELO capture logic
        const team1Objs = court.team1.map(id => playersById[id]).filter(Boolean);
        const team2Objs = court.team2.map(id => playersById[id]).filter(Boolean);
        const gameTypeId = detectGameType(team1Objs, team2Objs);
        const isSinglesGame = [4, 5, 7].includes(gameTypeId);

        for (const playerId of [...court.team1, ...court.team2]) {
          const player = playersById[playerId];
          if (!player) continue;

          const isTeam1 = court.team1.includes(playerId);
          const playerTeamIds = isTeam1 ? court.team1 : court.team2;
          const playerTeamObjs = playerTeamIds.map(id => playersById[id]).filter(Boolean);
          const pairType = detectPairType(playerTeamObjs, gameTypeId);

          preGameElos[playerId] = isSinglesGame
            ? getSinglesEloForComputation(player)
            : pairType === 'mixed'
              ? player.mixed_elo_rating
              : player.levels_elo_rating;
        }
      }

      // First update the local state with ELO calculations
      let updatedCourts;
      let updatedPlayers = []; // Store the updated player objects
      
      // Determine the winner based on scores
      const winner = score.team1Score > score.team2Score ? 'team1' : 'team2';
      
      const result = await updateMatchResult(courtAssignments || [], courtIndex, winner, score, currentClubId, playersById, playerWaitMetricsRef.current || {});
      updatedCourts = result;

      // Competitive normalized: DB + sync apply the post-score court (promotion or empty). Skip the
      // interim "winner badge" UI while finish_session_match + reload runs.
      const skipInterimFinishedCourtUi =
        !isSocialSession &&
        sessionId &&
        currentClubId &&
        typeof onAfterFinishMatchSync === 'function';
      if (!skipInterimFinishedCourtUi) {
        setCourtAssignments(result);
      }
        
        // Get the updated player objects from the court that was just processed
      // Use the updated court from result, not the old courtAssignments
      const court = result[courtIndex];
        if (court) {
          const allPlayerIds = [...court.team1, ...court.team2];
          updatedPlayers = allPlayerIds.map(id => playersById[id]).filter(Boolean);
        }

      // Record match history in state for matchmaking
      setMatchHistory(prevHistory => {
        const court = courtAssignments[courtIndex];
        if (!court) return prevHistory || [];
        const allPlayerIds = [...(court.team1 || []), ...(court.team2 || [])];
        // Build ELO snapshot
        const eloAfter = {};
        allPlayerIds.forEach(playerId => {
          const player = playersById[playerId];
          if (!player) return;
          eloAfter[playerId] = {
            levels_elo_rating: player.levels_elo_rating,
            mixed_elo_rating: player.mixed_elo_rating,
            singles_elo_rating: player.singles_elo_rating,
          };
        });
        return [
          ...(prevHistory || []),
          {
            players: allPlayerIds,
            team1: court.team1 || [], // Direct properties for normalized scoring
            team2: court.team2 || [], // Direct properties for normalized scoring
            teams: [court.team1 || [], court.team2 || []], // Keep for backward compatibility
            result: winner,
            score,
            timestamp: new Date().toISOString(),
            eloAfter
          }
        ];
      });

      // Use the updated player objects that were processed in updateMatchResult
      const allPlayers = updatedPlayers;
      
      // Safety check for court assignment
      if (!court) {
        console.error('Court assignment not found for index:', courtIndex, 'Available courts:', courtAssignments.length);
        if (scoreClaimActive && claimCourtNumberForRpc != null && sessionId) {
          await abortSessionCourtScoreSubmission(sessionId, claimCourtNumberForRpc);
        }
        return { needsGamesPlayedRefresh: true };
      }
      
      // Remove this optimistic update - games should only be incremented after successful database save
      // if (court) {
      //   allPlayers.forEach(player => {
      //     player.gamesPlayedThisSession = (player.gamesPlayedThisSession || 0) + 1;
      //   });
      // }

      // Save to database if sessionId is available, but do NOT fetch players or update global player state
      let game;
      // Prepare container for ELO deltas so it's available even if we skip RPC
      let eloDeltasFromRpc = {};

      if (sessionId && currentClubId && court) {
        const gcMeta = ensureGameCreationMeta(court?.gameCreation);
        const courtForMethod = { ...court, gameCreation: gcMeta };
        const { method: gameCreateMethod, algorithm: matchmakingAlgorithm } =
          resolveGameCreateMethodForCourt(courtForMethod);
        const scheduledGameIdForRpc = gcMeta?.scheduledGameId
          ? String(gcMeta.scheduledGameId).trim()
          : null;
        // commit_score_transition reads p_scheduled_game_id only when p_game_create_method is set.
        const gameCreateMethodForRpc = scheduledGameIdForRpc
          ? (gameCreateMethod === 'scheduled_altered' ? 'scheduled_altered' : 'scheduled')
          : (gameCreateMethod === 'scheduled' || gameCreateMethod === 'scheduled_altered'
            ? null
            : gameCreateMethod);

        // Detect game type from player gender info (needed for players_session_games pair_type)
        const team1Objs = (court.team1 || []).map(id => playersById[id]).filter(Boolean);
        const team2Objs = (court.team2 || []).map(id => playersById[id]).filter(Boolean);
        const gameTypeId = detectGameType(team1Objs, team2Objs);

        dlog('🔍 finish_session_match data:', { courtNumber: claimCourtNumberForRpc, gameTypeId, gameCreateMethod });

        const refillIntent = computeRefillIntent();

        const shouldUpdateElo =
          !isSocialSession && !isPegBoardSession && gameAffectsElo(gameCreateMethod);
        let pendingEloHistoryRecords = null;
        let pendingSinglesRatingUpdates = null;

        const isEloExempt = isSessionMensDoublesEloExemptLineup(
          court.team1 || [],
          court.team2 || [],
          playerWaitMetricsRef.current || {},
          playersById
        );

        if (shouldUpdateElo) {
          const preElo = createFinalEloHistoryRecords(
            allPlayers,
            null,
            sessionId,
            currentClubId,
            court.team1,
            court.team2,
            playersById,
            seasonId,
            preGameElos,
            {
              team1Score: score?.team1Score,
              team2Score: score?.team2Score,
              winningTeam: score?.winningTeam,
            },
            isEloExempt
          );
          pendingEloHistoryRecords = preElo.records;
          pendingSinglesRatingUpdates = preElo.singlesRatingUpdates;
        }

        const scoreEventId = crypto.randomUUID();

        if (isPegBoardSession) {
          const courtNum = claimCourtNumberForRpc ?? court.courtNumber ?? (courtIndex + 1);
          matchResult = await finishPegBoardMatchCmd(sessionId, courtNum, {
            clubId: currentClubId,
            seasonId: seasonId || null,
            team1PlayerIds: court.team1,
            team2PlayerIds: court.team2,
            team1Score: Number(score?.team1Score) || 0,
            team2Score: Number(score?.team2Score) || 0,
            winningTeam: score.winningTeam,
            gameTypeId: gameTypeId || null,
            startTime: normalizeMatchStartTime(court.startTime),
            endTime: new Date().toISOString(),
            matchmakingAlgorithm: matchmakingAlgorithm || 'peg_board_manual',
            tabInstanceId: tabInstanceId || null,
          });
        } else if (useServerMatchmaking) {
          const courtNum = claimCourtNumberForRpc ?? court.courtNumber ?? (courtIndex + 1);
          let expectedCourtVersion = courtForClaim?.version ?? court.version ?? null;
          const rosterFull =
            (court.team1?.length ?? 0) === playersPerSide &&
            (court.team2?.length ?? 0) === playersPerSide;
          // UI may show inProgress via ephemeral merge while DB still has in_progress=false.
          const skipPreScoreSync = shouldRunLocalHostPath(offlineQueue);
          if (rosterFull && !skipPreScoreSync) {
            try {
              const syncResult = await updateSingleCourtAtomic(
                sessionId,
                courtNum,
                'active',
                expectedCourtVersion ?? -1,
                {
                  inProgress: true,
                  team1: court.team1,
                  team2: court.team2,
                  gameCreation: court.gameCreation,
                },
                tabInstanceId || null
              );
              expectedCourtVersion = syncResult.newVersion;
            } catch (syncErr) {
              console.warn('[useCourtManagement] Pre-score in_progress sync (server may auto-start):', syncErr);
            }
          }
          const scorePayload = {
            sessionId,
            courtNumber:    courtNum,
            tabInstanceId:  tabInstanceId || null,
            team1PlayerIds: court.team1,
            team2PlayerIds: court.team2,
            team1Score:     Number(score?.team1Score) || 0,
            team2Score:     Number(score?.team2Score) || 0,
            winningTeam:    score.winningTeam ?? null,
            gameTypeId:     gameTypeId || null,
            startTime:      normalizeMatchStartTime(court.startTime),
            endTime:        new Date().toISOString(),
            scoreEventId,
            gameCreateMethod:     gameCreateMethodForRpc || null,
            matchmakingAlgorithm: matchmakingAlgorithm || null,
            scheduledGameId:      scheduledGameIdForRpc,
            eloUpdates: shouldUpdateElo && pendingEloHistoryRecords?.length
              ? eloRecordsToServerPayload(pendingEloHistoryRecords)
              : null,
            expectedCourtVersion,
          };
          const submitScore = () => completeSessionScoreCmd(scorePayload);
          if (shouldRunLocalHostPath(offlineQueue) && !isPegBoardSession) {
            matchResult = await submitLocalOfflineScore({
              offlineQueue,
              getOfflineLiveState: () => ({
                ...(typeof getOfflineLiveState === 'function' ? getOfflineLiveState() : {}),
                courtAssignments,
                nextAssignments,
                waitingList,
                playersById,
                sessionPlayerStatus,
                playerWaitMetrics,
                matchHistory,
                partnerships,
                scheduledGames,
              }),
              court,
              courtNum,
              scorePayload,
              pendingEloHistoryRecords,
            });
          } else {
            matchResult = await submitScore();
          }
        } else {
          // Legacy path: finish_session_match RPC with waitlist_order refill
          matchResult = await finishMatchCmd(
            sessionId,
            claimCourtNumberForRpc,
            {
              clubId:               currentClubId,
              seasonId:             seasonId || null,
              team1PlayerIds:       court.team1,
              team2PlayerIds:       court.team2,
              team1Score:           Number(score?.team1Score) || 0,
              team2Score:           Number(score?.team2Score) || 0,
              winningTeam:          score.winningTeam,
              gameTypeId:           gameTypeId || null,
              startTime:            normalizeMatchStartTime(court.startTime),
              endTime:              new Date().toISOString(),
              gameCreateMethod:     gameCreateMethodForRpc || null,
              matchmakingAlgorithm: matchmakingAlgorithm || null,
              scheduledGameId:      scheduledGameIdForRpc,
              tabInstanceId:        tabInstanceId || null,
              // nextUpSnapshot removed (Phase 4): DB is source of truth after quiescence gate
              refillIntent,
            }
          );
        }
        scoreTimer.mark(useServerMatchmaking ? 'completeSessionScore' : 'finishMatch');

        if (!matchResult.ok) {
          if (matchResult.code === 'MATCH_NOT_ACTIVE') {
            throw new ScoreSubmissionClaimError('not_in_progress', { message: matchResult.message });
          }
          if (
            matchResult.code === 'PENDING_OTHER' ||
            matchResult.code === 'NOT_CLAIMED' ||
            matchResult.code === 'LOCK_CONTENTION' ||
            matchResult.code === 'TRANSITION_ALREADY_PREPARED'
          ) {
            throw new ScoreSubmissionClaimError('pending_other', { message: matchResult.message });
          }
          if (matchResult.code === 'VERSION_MISMATCH') {
            throw new ScoreSubmissionClaimError('version_mismatch', { message: matchResult.message });
          }
          throw new Error(matchResult.message || 'Failed to record match result.');
        }

        shouldAutoRotateAfterScore = true;
        willSyncFromDbAfterFinish = Boolean(
          !isSocialSession && sessionId && currentClubId && onAfterFinishMatchSync && !matchResult.local
        );

        // finishMatchCmd handles clearing the claim atomically; no separate finalize needed
        game = { id: matchResult.gameId };
        scoreClaimActive = false;
        dlog('✅ finish_session_match completed, gameId:', matchResult.gameId);

        // Scheduled games transition to completed inside finish_session_match (linked session_games.scheduled_game_id).

        if (claimCourtNumberForRpc != null) {
          serverClearedActiveCourtsRef.current.add(claimCourtNumberForRpc);
          serverClearedCourtRosterSigRef.current[claimCourtNumberForRpc] = courtRosterSig(court);
          if (matchResult.courtVersion != null) {
            setCourtAssignments(prev => prev.map(c =>
              c.courtNumber === claimCourtNumberForRpc
                ? { ...c, version: matchResult.courtVersion }
                : c
            ));
          }
        }

        const applyEloToPlayerObjects = (singlesRatingUpdates) => {
          if (singlesRatingUpdates) {
            const t1Objs = court.team1.map(id => playersById[id]).filter(Boolean);
            const t2Objs = court.team2.map(id => playersById[id]).filter(Boolean);
            const gidSingles = detectGameType(t1Objs, t2Objs);
            allPlayers.forEach(player => {
              const u = singlesRatingUpdates[player.id];
              if (!u) return;
              player.singles_elo_rating = Math.round(u.newRating);
              if (gidSingles === 4 || gidSingles === 5) {
                player.singles_games_played = (Number(player.singles_games_played) || 0) + 1;
              }
            });
          }
          const t1Objs = court.team1.map(id => playersById[id]).filter(Boolean);
          const t2Objs = court.team2.map(id => playersById[id]).filter(Boolean);
          const gameTypeIdResolved = detectGameType(t1Objs, t2Objs);
          allPlayers.forEach(player => {
            const isTeam1 = court.team1.some(p => p.id === player.id);
            const playerTeam = isTeam1 ? court.team1 : court.team2;
            const playerTeamObjs = playerTeam.map(id => playersById[id]).filter(Boolean);
            const pairType = detectPairType(playerTeamObjs, gameTypeIdResolved);
            player.pairType = [4, 5, 7].includes(gameTypeIdResolved) ? 'singles' : pairType;
            if ([4, 5, 7].includes(gameTypeIdResolved)) {
              player.newElo = player.singles_elo_rating;
            } else {
              player.newElo = pairType === 'mixed' ? player.mixed_elo_rating : player.levels_elo_rating;
            }
          });
        };

        const pushEloPlayerUpdates = () => {
          const updatedPlayersById = { ...playersById };
          allPlayers.forEach(player => {
            if (updatedPlayersById[player.id]) {
              updatedPlayersById[player.id] = {
                ...updatedPlayersById[player.id],
                mixed_elo_rating: player.mixed_elo_rating,
                levels_elo_rating: player.levels_elo_rating,
                singles_elo_rating: player.singles_elo_rating,
                singles_games_played: player.singles_games_played,
                gamesPlayedThisSession: player.gamesPlayedThisSession,
              };
            }
          });
          if (updatePlayers) updatePlayers(updatedPlayersById);
        };

        const serverEloApplied = useServerMatchmaking && matchResult.eloDeltas
          && Object.keys(matchResult.eloDeltas).length > 0;

        if (!isSocialSession && shouldUpdateElo) {
          if (serverEloApplied) {
            eloDeltasFromRpc = Object.keys(matchResult.eloDeltas).reduce((acc, playerId) => {
              const d = matchResult.eloDeltas[playerId];
              acc[playerId] = typeof d === 'number' ? d : (d?.total ?? 0);
              return acc;
            }, {});
            applyEloToPlayerObjects(pendingSinglesRatingUpdates);
            pushEloPlayerUpdates();
          } else if (
            !serverEloApplied &&
            game?.id &&
            pendingEloHistoryRecords?.length &&
            !isGameEloPersisted(game.id)
          ) {
            const recordsWithGameId = pendingEloHistoryRecords.map((r) => ({
              ...r,
              session_game_id: game.id,
            }));
            applyEloToPlayerObjects(pendingSinglesRatingUpdates);
            void persistScoreEloAsync({
              gameId: game.id,
              eloHistoryRecords: recordsWithGameId,
              testMode,
              preGameElos,
              onError: (msg) => {
                if (typeof onScoreEloError === 'function') onScoreEloError(msg);
                else setError(msg);
              },
              onSuccess: (rpcDeltas) => {
                eloDeltasFromRpc = { ...rpcDeltas };
                pushEloPlayerUpdates();
              },
            });
          }
        }
      }
      // We'll use the game history data for ELO history instead of separate recordEloHistory call
      // Do NOT updatePlayers or fetchPlayers here
      // After DB update, calculate and store deltas as eloDeltas on the finished court
      let eloDeltas = {};
      
      // Check if this is an unbalanced game - if so, eloDeltas should be empty (no ELO changes)
      // Use the updated court from updateMatchResult which has unbalancedInfo if applicable
      const isUnbalancedGame = result[courtIndex]?.unbalancedInfo !== undefined;
      
      // First, check if eloDeltas with baseDelta/nudge breakdown is already in the result from updateMatchResult
      if (result[courtIndex]?.eloDeltas && typeof result[courtIndex].eloDeltas === 'object') {
        eloDeltas = { ...result[courtIndex].eloDeltas };
      } else if (!isUnbalancedGame) {
        // Only calculate ELO deltas for balanced games if not already set
        if (typeof eloDeltasFromRpc === 'object' && Object.keys(eloDeltasFromRpc).length > 0) {
          // Convert simple number deltas to object format for consistency
          eloDeltas = Object.keys(eloDeltasFromRpc).reduce((acc, playerId) => {
            const total = eloDeltasFromRpc[playerId];
            acc[playerId] = {
              total,
              baseDelta: total, // If from RPC, we don't have breakdown, so use total for both
              nudge: 0
            };
            return acc;
          }, {});
        } else if (courtAssignments[courtIndex]) {
        for (const playerId of [...courtAssignments[courtIndex].team1, ...courtAssignments[courtIndex].team2]) {
          const oldElo = preGameElos[playerId];
          let newElo;
          
          if (playersById && playersById[playerId]) {
            const isTeam1 = court.team1.includes(playerId);
            const playerTeamIds = isTeam1 ? court.team1 : court.team2;
            const playerTeamObjs = playerTeamIds.map(id => playersById[id]).filter(Boolean);
            const team1Objs = court.team1.map(id => playersById[id]).filter(Boolean);
            const team2Objs = court.team2.map(id => playersById[id]).filter(Boolean);
            const gameTypeId = detectGameType(team1Objs, team2Objs);
            const pairType = detectPairType(playerTeamObjs, gameTypeId);
            const pRow = playersById[playerId];
            if ([4, 5, 7].includes(gameTypeId)) {
              newElo = getSinglesEloForComputation(pRow);
            } else {
              newElo = pairType === 'mixed' ? pRow.mixed_elo_rating : pRow.levels_elo_rating;
            }
          } else {
              newElo = oldElo;
          }
          
          if (oldElo !== undefined && newElo !== undefined) {
              const total = Math.round(newElo - oldElo);
              eloDeltas[playerId] = {
                total,
                baseDelta: total, // Fallback: use total if we don't have breakdown
                nudge: 0
              };
          }
        }
      }
      }
      // For unbalanced games, eloDeltas remains empty {} - no ELO changes should be displayed

      const willAutoRotateFromScore =
        shouldAutoRotateAfterScore &&
        autoRotateAfterScoreSubmission &&
        !willSyncFromDbAfterFinish;

      const skipEloPersistenceOnFinishedCourt =
        willAutoRotateFromScore || willSyncFromDbAfterFinish;

      // Store the calculated ELO deltas in the court assignment (skip if rotation immediately replaces finished slots)
      if (!skipEloPersistenceOnFinishedCourt) {
        setCourtAssignments(prev => {
          const updated = [...(prev || [])];
          if (updated[courtIndex]) {
            updated[courtIndex] = {
              ...updated[courtIndex],
              eloDeltas,
              showEloChange: !isUnbalancedGame // Only show ELO changes for balanced games
            };
          }
          return updated;
        });
      }
      // eloDeltas now persists until rotation (or is carried in snapshot when auto-rotating)
      
      // Add the completed game to history
      // Note: 'court' variable is already declared above on line 513
      
      const historyGameData = {
        courtId: court.courtNumber,
        team1: [...(court.team1 || [])],
        team2: [...(court.team2 || [])],
        team1Score: Number(score?.team1Score) || 0,
        team2Score: Number(score?.team2Score) || 0,
        timestamp: new Date().toISOString(),
        eloDeltas: eloDeltas,
        sessionId: sessionId,
        clubId: currentClubId,
        gameId: game?.id,
        matchHistory: matchHistory,
        playersById: playersById,
        currentClubId: currentClubId,
        sessionPlayerStatus: sessionPlayerStatus,
        fetchPlayers: fetchPlayers,
        season_id: seasonId
      };
      
      // Update game history (synchronous)
      addGameToHistory(historyGameData);
      
      // Handle wait counter increment with deduplication to prevent StrictMode double-calls
      // Create a stable key based on the game session ID (which is unique per game)
      const completionKey = game?.id ? `game-${game.id}` : `court-${courtIndex}-${score.team1Score}-${score.team2Score}`;
      
      dlog('🔥 WAIT COUNTER DEBUG: Court', courtIndex, 'finished - incrementing for', waitingList.length, 'waiting players');
      dlog('🔥 WAIT COUNTER DEBUG: Completion key:', completionKey);
      
      if (!willSyncFromDbAfterFinish) {
        // Move deduplication check INSIDE the state setter to prevent StrictMode from running the increment logic twice
        setPlayerWaitMetrics(prevMetrics => {
          if (processedGameCompletions.current.has(completionKey)) {
            dlog('🔥 WAIT COUNTER DEBUG: [DEDUP] ✅ Skipping duplicate - already processed', completionKey);
            return prevMetrics;
          }

          processedGameCompletions.current.add(completionKey);

          const newMetrics = { ...prevMetrics };
          const currentTime = Date.now();

          dlog('🔥 WAIT COUNTER DEBUG: Before increment - sample counters:',
            waitingList.slice(0, 3).map(id => `${id.slice(0,8)}: ${newMetrics[id]?.courtsObserved || 0}`)
          );

          waitingList.forEach(playerId => {
            if (!newMetrics[playerId]) {
              newMetrics[playerId] = {
                courtsObserved: 0,
                lastCourtUpdate: currentTime,
                consecutiveWaits: 0
              };
            }
            newMetrics[playerId].courtsObserved += 1;
            newMetrics[playerId].lastCourtUpdate = currentTime;
          });

          dlog('🔥 WAIT COUNTER DEBUG: After increment - sample counters:',
            waitingList.slice(0, 3).map(id => `${id.slice(0,8)}: ${newMetrics[id]?.courtsObserved || 0}`)
          );

          return newMetrics;
        });

        if (sessionId && waitingList.length > 0 && !isBrowserOffline() && !offlineQueueIsPending(offlineQueue)) {
          import('../services/sessionStateService').then(SessionStateService => {
            return SessionStateService.incrementCourtsWatched(sessionId, waitingList);
          }).then(() => {
            dlog('✅ COOLDOWN: Courts watched incremented in database for', waitingList.length, 'waiting players');

            return import('../services/sessionStateService').then(SessionStateService => {
              return SessionStateService.loadNormalizedState(sessionId);
            });
          }).then(state => {
            if (state && state.playerWaitMetrics) {
              dlog('✅ COOLDOWN: Reloaded wait metrics after increment');
              playerWaitMetricsRef.current = state.playerWaitMetrics;
              setPlayerWaitMetrics(state.playerWaitMetrics);
            }
          }).catch(error => {
            console.error('❌ COOLDOWN: Failed to increment/reload courts watched:', error);
          });
        }
      }

      const notifyCourtFreshRotationHighlight = () => {
        if (typeof onCourtFreshRotationHighlight !== 'function') return;
        if (isSocialSession) return;
        const n = claimCourtNumberForRpc;
        if (n == null) return;
        try {
          onCourtFreshRotationHighlight(n);
        } catch (e) {
          console.error('[useCourtManagement] onCourtFreshRotationHighlight failed:', e);
        }
      };

      if (matchResult?.local) {
        if (Array.isArray(matchResult.courtAssignments)) {
          setCourtAssignments(matchResult.courtAssignments);
        }
        if (Array.isArray(matchResult.nextAssignments)) {
          setNextAssignments(matchResult.nextAssignments);
        }
        if (Array.isArray(matchResult.waitingList)) {
          setWaitingList(matchResult.waitingList);
        }
        if (Array.isArray(matchResult.scheduledGames) && typeof onScheduledGamesChange === 'function') {
          onScheduledGamesChange(matchResult.scheduledGames);
        }
        const consumeIds = [
          ...(matchResult.completedScheduledGameIds || []),
          ...(matchResult.placedScheduledGameIds || []),
        ];
        if (consumeIds.length && typeof onConsumeScheduledGames === 'function') {
          void Promise.resolve(
            onConsumeScheduledGames(consumeIds, {
              completed: (matchResult.completedScheduledGameIds || []).length > 0,
              completedIds: matchResult.completedScheduledGameIds || [],
              placedIds: matchResult.placedScheduledGameIds || [],
            })
          ).catch((err) => {
            console.error('[useCourtManagement] Scheduled game consume after local score failed:', err);
          });
        }
        try {
          onScoreStatsIncrement?.({
            team1: court.team1 || [],
            team2: court.team2 || [],
            winningTeam: score?.winningTeam ?? null,
            gameId: matchResult?.gameId,
          });
        } catch (e) {
          console.error('[useCourtManagement] onScoreStatsIncrement failed:', e);
        }
        processedGameCompletions.current.add(completionKey);
        notifyCourtFreshRotationHighlight();
        needsGamesPlayedRefresh = false;
      } else if (willSyncFromDbAfterFinish) {
        const syncContext = {
          courtNumber: claimCourtNumberForRpc,
          gameId: matchResult?.gameId,
          courtVersion: matchResult?.courtVersion,
          promoted: matchResult?.promoted,
          promotedFromSlot: matchResult?.promotedFromSlot,
          fills: matchResult?.fills,
        };

        // Phase B: prefer authoritative result from EF when available.
        // applyScoreCompletedResult sets state from the post-commit DB snapshot,
        // replacing the optimistic layout with values that are already correct.
        const hasCommandResult = matchResult?.result && typeof onApplyScoreCommandResult === 'function';
        if (hasCommandResult) {
          try {
            onApplyScoreCommandResult(matchResult.result);
          } catch (e) {
            console.error('[useCourtManagement] onApplyScoreCommandResult failed:', e);
          }
          // Still notify the optimistic hook with the sync context (epoch bump etc.)
          try {
            onApplyOptimisticPostScore?.(syncContext, null);
          } catch (e) {
            console.error('[useCourtManagement] onApplyOptimisticPostScore failed:', e);
          }
        } else {
          // Fallback: optimistic apply (server matchmaking not yet returning result)
          const optimistic = applyOptimisticPostScoreLayout(
            { courtAssignments, nextAssignments },
            syncContext,
          );
          setCourtAssignments(optimistic.courtAssignments);
          setNextAssignments(optimistic.nextAssignments);
          try {
            onApplyOptimisticPostScore?.(syncContext, optimistic);
          } catch (e) {
            console.error('[useCourtManagement] onApplyOptimisticPostScore failed:', e);
          }
        }

        try {
          onScoreStatsIncrement?.({
            team1: court.team1 || [],
            team2: court.team2 || [],
            winningTeam: score?.winningTeam ?? null,
            gameId: matchResult?.gameId,
          });
        } catch (e) {
          console.error('[useCourtManagement] onScoreStatsIncrement failed:', e);
        }

        processedGameCompletions.current.add(completionKey);
        notifyCourtFreshRotationHighlight();
        needsGamesPlayedRefresh = false;

        void Promise.resolve(onAfterFinishMatchSync(syncContext))
          .then(() => {
            scoreTimer.mark('postScoreSync');
          })
          .catch((e) => {
            console.error('[useCourtManagement] onAfterFinishMatchSync failed:', e);
          });
      } else if (willAutoRotateFromScore) {
        const courtsWithElo = result.map((c, i) =>
          i === courtIndex
            ? { ...c, eloDeltas, showEloChange: !isUnbalancedGame }
            : c
        );
        rotateAndRefillCourts({
          courtAssignments: courtsWithElo,
          nextAssignments,
          waitingList,
          numberOfUpcomingCourts,
        });
        try {
          await Promise.resolve(onAfterAutoRotateFromScore?.());
          notifyCourtFreshRotationHighlight();
          needsGamesPlayedRefresh = false;
        } catch (e) {
          console.error('[useCourtManagement] onAfterAutoRotateFromScore failed:', e);
        }
      }

      return { needsGamesPlayedRefresh };
    } catch (error) {
      if (scoreClaimActive && claimCourtNumberForRpc != null && sessionId) {
        await abortSessionCourtScoreSubmission(sessionId, claimCourtNumberForRpc);
      }
      console.error('Error in updateMatchResultHandler:', error);
      if (error?.name === 'ScoreSubmissionClaimError' && error?.message) {
        setError(error.message);
      } else if (testMode) {
        setError('Error in Edge Function test: ' + error.message);
      } else {
        setError('Failed to save game result. ELO changes may not be persisted.');
      }
      throw error; // Re-throw so CourtAssignment can show an error to the user
    } finally {
      scoreTimer.finish();
      // Release score-prep lock so deferred next_up writes and realtime merges can resume.
      releaseScorePrepLock?.();
      inFlightScoreSubmissions.current.delete(courtIndex);
    }
  }, [
    courtAssignments,
    currentClubId,
    sessionId,
    playersById,
    playersPerSide,
    matchHistory,
    sessionPlayerStatus,
    seasonId,
    nextAssignments,
    waitingList,
    numberOfUpcomingCourts,
    rotateAndRefillCourts,
    autoRotateAfterScoreSubmission,
    onAfterAutoRotateFromScore,
    onAfterFinishMatchSync,
    onApplyOptimisticPostScore,
    onApplyScoreCommandResult,
    onScoreStatsIncrement,
    onScoreRecordingPreviewOpen,
    onCourtFreshRotationHighlight,
    onConsumeScheduledGames,
    onScheduledGamesChange,
    isSocialSession,
    isPegBoardSession,
    tabInstanceId,
    acquireScorePrepLock,
    releaseScorePrepLock,
    onScoreEloError,
    offlineQueue,
    getOfflineLiveState,
  ]);

  /**
   * Removes a court and moves its players to the waiting list
   * @param {number} courtIndex - Index of the court to remove
   */
  const removeCourtHandler = useCallback((courtIndex) => {
    dlog('🔄 removeCourtHandler called with courtIndex:', courtIndex);
    dlog('🔄 Current courtAssignments before removal:', courtAssignments);
    setCourtAssignments(prev => {
      const result = removeCourt(prev || [], waitingList || [], courtIndex, {
        waitlistFront: isPegBoardSession,
      });
      dlog('🔄 removeCourt result:', result);
      setWaitingList(result.waitingList);
      return result.courts;
    });
  }, [waitingList, courtAssignments, isPegBoardSession]);

  /**
   * Removes a player from a specific court
   * @param {Object} player - Player to remove
   * @param {number} team - Team number (1 or 2)
   * @param {number} courtNumber - Court number to remove player from
   */
  const removePlayerFromCourtHandler = useCallback((player, team, courtNumber) => {
    setCourtAssignments(prev => {
      const result = removePlayerFromCourt(prev || [], waitingList || [], player, team, courtNumber);
      setWaitingList(result.waitingList);
      return result.courts;
    });
  }, [waitingList]);

  // ===== COURT COUNT MANAGEMENT =====

  /**
   * Calculates the maximum number of courts possible with current active players
   * @returns {number} Maximum number of courts based on available players (minimum 1, max 10 for practical sessions)
   */
  const getMaxCourts = useCallback(() => {
    // Calculate based on ALL active players in the session (not just waiting list + next assignments)
    const totalActivePlayers = activePlayers.filter(player => 
      sessionPlayerStatus[player.id] === 'active'
    );
    
    const maxCourtsFromPlayers = Math.ceil(totalActivePlayers.length / playersPerMatch);
    
    // CRITICAL: Don't allow adding courts if there aren't enough players for a new match
    // If we have 2 full courts (8 players) and only 1 player waiting, we can't create a 3rd court
    const playersOnCourts = courtAssignments.flatMap(c => [...c.team1, ...c.team2]).length;
    const playersInWaiting = waitingList.length;
    const playersInUpcoming = nextAssignments.flatMap(match => [...match.team1, ...match.team2]).length;
    const totalAvailableForNewCourt = playersInWaiting + playersInUpcoming;
    
    // If we can't create a new match with available players, don't allow more courts
    if (totalAvailableForNewCourt < playersPerMatch && courtAssignments.length > 0) {
      return courtAssignments.length;
    }
    
    // Set a reasonable limit for badminton sessions (max 10 courts)
    const practicalLimit = 10;
    
    // Return the smaller of the two limits, but at least 1
    const result = Math.max(1, Math.min(maxCourtsFromPlayers, practicalLimit));
    return result;
  }, [
    activePlayers,
    sessionPlayerStatus,
    courtAssignments,
    waitingList,
    nextAssignments,
    playersPerMatch,
  ]);

  /**
   * Adjusts the number of available courts (database-driven approach)
   * Court count is derived from session_courts table, allowing gaps in numbering
   * @param {number} increment - Amount to increment/decrement by
   */
  const applyActiveCourtCountLocally = useCallback((increment) => {
    const result = applyLocalActiveCourtCount({
      courtAssignments,
      currentCount: numberOfCourtsState,
      increment,
      maxCourts: getMaxCourts(),
    });
    if (!result.ok) {
      setError(result.reason);
      return null;
    }
    if (result.noChange) return result;
    setCourtAssignments(result.courtAssignments);
    setNumberOfCourtsState(result.numberOfCourts);
    return result;
  }, [courtAssignments, getMaxCourts, numberOfCourtsState]);

  const enqueueActiveCourtCount = useCallback(async (result) => {
    if (!offlineQueue?.enqueue || !result || result.noChange) return;
    const extras = typeof getOfflineLiveState === 'function' ? getOfflineLiveState() : {};
    await offlineQueue.enqueue({
      type: OFFLINE_COMMAND_TYPES.COURT_COUNT,
      target: 'active',
      newCount: result.numberOfCourts,
      courtNumbersAdded: result.courtNumbersAdded || [],
      courtNumberRemoved: result.courtNumberRemoved ?? null,
    }, {
      ...extras,
      courtAssignments: result.courtAssignments,
    });
  }, [getOfflineLiveState, offlineQueue]);

  const adjustCourtCountHandler = useCallback(async (increment) => {
    dlog('🔄 adjustCourtCountHandler called with increment:', increment);

    if (offlineQueue && shouldRunLocalHostPath(offlineQueue)) {
      const local = applyActiveCourtCountLocally(increment);
      await enqueueActiveCourtCount(local);
      return;
    }
    
    try {
      // Dynamically import SessionStateService
      const SessionStateService = await import('../services/sessionStateService');
      
      // Get current court count from database (source of truth)
      const currentDbCourtCount = await SessionStateService.getActiveCourtCount(sessionId);
      const newCount = currentDbCourtCount + increment;
      const maxCourts = getMaxCourts();
      const finalCount = Math.min(Math.max(1, newCount), maxCourts);
      
      dlog('🔄 adjustCourtCountHandler - DB count:', currentDbCourtCount, 'newCount:', newCount, 'finalCount:', finalCount);
      
      // Prevent reducing below number of in-progress courts
      const inProgressCourts = courtAssignments.filter(c => c.inProgress).length;
      if (finalCount < inProgressCourts) {
        setError(`Cannot reduce courts below the number of courts with games in progress (${inProgressCourts}). Finish those games first.`);
        return;
      }
      
      if (finalCount === currentDbCourtCount) {
        dlog('🔄 adjustCourtCountHandler - No change needed');
        return;
      }
      
      if (finalCount > currentDbCourtCount) {
        // INCREASING: Restore or create courts
        // Strategy: Fill gaps first, then add at the end
        const courtsToAdd = finalCount - currentDbCourtCount;
        dlog('➕ Adding', courtsToAdd, 'court(s)');
        
        const createdCourtNumbers = [];
        
        // First, fill any gaps in court numbering (e.g., if courts 1, 2, 4 exist, fill gap at 3)
        for (let i = 0; i < courtsToAdd; i++) {
          const gapCourtNumber = await SessionStateService.findFirstCourtGap(sessionId);
          
          if (gapCourtNumber) {
            // Fill the gap
            await SessionStateService.restoreOrCreateCourt(sessionId, gapCourtNumber);
            createdCourtNumbers.push(gapCourtNumber);
            dlog('✅ Filled gap - Court', gapCourtNumber, 'restored/created');
          } else {
            // No gaps - add at the end
            const highestCourtNumber = await SessionStateService.getHighestCourtNumber(sessionId);
            const newCourtNumber = highestCourtNumber + 1;
            await SessionStateService.restoreOrCreateCourt(sessionId, newCourtNumber);
            createdCourtNumbers.push(newCourtNumber);
            dlog('✅ Added new court', newCourtNumber, 'at the end');
          }
        }
        
        // Update local state: Add the newly created courts
        setCourtAssignments(prev => {
          const updated = [...prev];
          createdCourtNumbers.forEach(courtNumber => {
            // Check if court already exists in local state
            if (!updated.find(c => c.courtNumber === courtNumber)) {
              updated.push({
                courtNumber,
                team1: [],
                team2: [],
                inProgress: false,
                winner: null,
                score: null,
                startTime: null,
                gameCreation: {
                  origin: null,
                  algorithm: null,
                  isAltered: false,
                  scheduledGameId: null
                }
              });
            }
          });
          // Sort by court number to maintain order
          return updated.sort((a, b) => a.courtNumber - b.courtNumber);
        });
      } else {
        // DECREASING: Only allow reducing if the highest court has in_progress = false and is empty
        // This ensures predictable behavior and prevents accidentally removing lower-numbered courts
        const courtsToRemove = currentDbCourtCount - finalCount;
        dlog('➖ Attempting to remove', courtsToRemove, 'court(s)');
        
        // Check if we can remove the highest court (must be empty and not in progress)
        const canRemoveCheck = await SessionStateService.canRemoveHighestCourt(sessionId);
        
        if (!canRemoveCheck.canRemove) {
          setError(canRemoveCheck.reason);
          return;
        }
        
        // Only allow removing one court at a time for safety
        if (courtsToRemove > 1) {
          setError('Can only reduce one court at a time. The highest court must be empty and not in progress.');
          return;
        }
        
        // Delete the highest court from database
        // Since we've verified it's empty and not in progress, it's safe to delete
        await SessionStateService.deleteCourt(sessionId, canRemoveCheck.highestCourtNumber);
        dlog('✅ Deleted highest court', canRemoveCheck.highestCourtNumber, 'from database (prevents reappearing on reload)');
        
        // Update local state: Remove the deleted court
        setCourtAssignments(prev => 
          prev.filter(c => c.courtNumber !== canRemoveCheck.highestCourtNumber)
        );
      }
      
      // Update local state immediately for UI responsiveness
      setNumberOfCourtsState(finalCount);
      
    } catch (error) {
      console.error('❌ adjustCourtCountHandler error:', error);
      setError(`Failed to adjust court count: ${error.message}`);
    }
  }, [
    applyActiveCourtCountLocally,
    courtAssignments,
    enqueueActiveCourtCount,
    getMaxCourts,
    offlineQueue,
    sessionId,
  ]);

  // Compute whether reduce-courts button should be enabled (mirrors adjustCourtCountHandler / canRemoveHighestCourt logic)
  const { canReduceCourts, reduceCourtsReason } = useMemo(() => {
    const currentCount = numberOfCourtsState;
    if (currentCount <= 1) {
      return { canReduceCourts: false, reduceCourtsReason: 'Sessions must have at least 1 court' };
    }
    const inProgressCourts = courtAssignments.filter(c => c.inProgress).length;
    const newCount = currentCount - 1;
    if (newCount < inProgressCourts) {
      return {
        canReduceCourts: false,
        reduceCourtsReason: `Cannot reduce courts below the number of courts with games in progress (${inProgressCourts}). Finish those games first.`,
      };
    }
    const sorted = [...courtAssignments].sort((a, b) => (a.courtNumber || 0) - (b.courtNumber || 0));
    const highestCourt = sorted[sorted.length - 1];
    if (!highestCourt) {
      return { canReduceCourts: true, reduceCourtsReason: null };
    }
    if (highestCourt.inProgress === true) {
      return {
        canReduceCourts: false,
        reduceCourtsReason: `Court ${highestCourt.courtNumber} has a game in progress. Finish the game first.`,
      };
    }
    const hasPlayers = (highestCourt.team1?.length || 0) + (highestCourt.team2?.length || 0) > 0;
    if (hasPlayers) {
      return {
        canReduceCourts: false,
        reduceCourtsReason: `Court ${highestCourt.courtNumber} must be empty before reducing. Remove players first.`,
      };
    }
    return { canReduceCourts: true, reduceCourtsReason: null };
  }, [numberOfCourtsState, courtAssignments]);

  // Legacy handler for backward compatibility (kept for now but will be replaced)
  const adjustCourtCountHandlerLegacy = useCallback((increment) => {
    dlog('🔄 adjustCourtCountHandlerLegacy called with increment:', increment);
    dlog('🔄 Current courtAssignments.length:', courtAssignments.length);
    dlog('🔄 Current numberOfCourtsState:', numberOfCourtsState);
    
    setNumberOfCourtsState(prev => {
      const newCount = adjustCourtCount(prev, increment);
      const maxCourts = getMaxCourts();
      // Ensure we don't exceed the maximum possible courts
      const finalCount = Math.min(newCount, maxCourts);
      // Prevent reducing below number of in-progress courts
      const inProgressCourts = courtAssignments.filter(c => c.inProgress).length;
      
      dlog('🔄 adjustCourtCountHandler - prev:', prev, 'newCount:', newCount, 'finalCount:', finalCount);
      dlog('🔄 adjustCourtCountHandler - inProgressCourts:', inProgressCourts);
      
      if (finalCount < inProgressCourts) {
        setError(`Cannot reduce courts below the number of courts with games in progress (${inProgressCourts}). Finish those games first.`);
        return prev; // Do not change court count
      }
      if (courtsAssigned && activePlayers.length >= 2) {
        if (finalCount > courtAssignments.length) {
          // --- BEGIN REFACTOR ---
          let updatedCourts = [...courtAssignments];
          let updatedNextAssignments = [...nextAssignments];
          let updatedWaitingList = [...waitingList];
          let courtsToAdd = finalCount - updatedCourts.length;
          // Helper function to get max court number from courts array
          // This ensures we use actual max court number, not array length (handles empty courts)
          const getMaxCourtNumber = (courts) => {
            if (!courts || courts.length === 0) return 0;
            const courtNumbers = courts.map(c => c.courtNumber).filter(num => num != null && num > 0);
            return courtNumbers.length > 0 ? Math.max(...courtNumbers) : 0;
          };

          // Promote upcoming matches to new courts, one at a time
          while (courtsToAdd > 0 && updatedNextAssignments.length > 0) {
            const nextCourtNumber = getMaxCourtNumber(updatedCourts) + 1;
            dlog('🔥 Promoting upcoming match to court:', nextCourtNumber);
            dlog('🔥 Before promotion - updatedNextAssignments:', updatedNextAssignments);
            
            const promotedCourt = updatedNextAssignments.shift();
            dlog('🔥 Promoted court:', promotedCourt);
            dlog('🔥 After shift - updatedNextAssignments:', updatedNextAssignments);
            
            promotedCourt.courtNumber = nextCourtNumber;
            promotedCourt.winner = null;
            promotedCourt.score = null;
            markCourtMatchStarted(promotedCourt);
            updatedCourts.push(promotedCourt);
            // Remove promoted players from waiting list
            const promotedPlayerIds = [...promotedCourt.team1, ...promotedCourt.team2];
            updatedWaitingList = updatedWaitingList.filter(id => !promotedPlayerIds.includes(id));
            courtsToAdd--;
          }
          // If more courts needed, try to create from waiting list (only if more than 4 players)
          while (courtsToAdd > 0 && updatedWaitingList.length > 4) {
            dlog(`🏸 Attempting to create court from ${updatedWaitingList.length} waiting players`);
            const gamesPlayedThisSession = {};
            updatedWaitingList.forEach(id => {
              const p = playersById[id];
              if (p) gamesPlayedThisSession[id] = p.gamesPlayedThisSession || 0;
            });
            const sessionState = buildSessionState({
              gamesPlayedThisSession,
              courtAssignments: updatedCourts
            });
            const result = callMatchmaking(updatedWaitingList, 1, sessionState, playersById, partnerships, [], gameTypeRestriction, playerCategories);
            if (result.courts.length > 0) {
              const newCourt = result.courts[0];
              newCourt.courtNumber = getMaxCourtNumber(updatedCourts) + 1;
              newCourt.winner = null;
              newCourt.score = null;
              markCourtMatchStarted(newCourt);
              updatedCourts.push(newCourt);
              // Remove those players from waiting list
              const newCourtPlayerIds = [...newCourt.team1, ...newCourt.team2];
              updatedWaitingList = updatedWaitingList.filter(id => !newCourtPlayerIds.includes(id));
              courtsToAdd--;
              dlog(`✅ Court created successfully. Remaining waiting players: ${updatedWaitingList.length}`);
            } else {
              dlog(`❌ Failed to create court. Result:`, result);
              break;
            }
          }
          
          // If still need more courts but not enough players, create empty courts
          // This allows users to manually add players later
          while (courtsToAdd > 0) {
            const nextCourtNumber = getMaxCourtNumber(updatedCourts) + 1;
            const emptyCourt = {
              courtNumber: nextCourtNumber,
              team1: [],
              team2: [],
              inProgress: false,
              winner: null,
              score: null,
              startTime: null,
              gameCreation: {
                origin: null,
                algorithm: null,
                isAltered: false,
                scheduledGameId: null
              }
            };
            updatedCourts.push(emptyCourt);
            courtsToAdd--;
            dlog(`✅ Created empty court ${nextCourtNumber} (can be filled manually)`);
          }
          // After all promotions/creations, generate a new upcoming match if enough players remain
          let newUpcoming = [];
          dlog('🔥 adjustCourtCountHandler - Checking for new upcoming match. Waiting list length:', updatedWaitingList.length);
          
          if (updatedWaitingList.length >= playersPerMatch) {
            dlog('🔥 adjustCourtCountHandler - Generating new upcoming match with', updatedWaitingList.length, 'players');
            const gamesPlayedThisSession = {};
            updatedWaitingList.forEach(id => {
              const p = playersById[id];
              if (p) gamesPlayedThisSession[id] = p.gamesPlayedThisSession || 0;
            });
            const sessionState = buildSessionState({
              gamesPlayedThisSession,
              courtAssignments: updatedCourts
            });
            const result = callMatchmaking(updatedWaitingList, 1, sessionState, playersById, partnerships, [], gameTypeRestriction, playerCategories);
            if (result.courts.length > 0) {
              newUpcoming = result.courts;
              // Remove those players from waiting list
              const upcomingPlayerIds = newUpcoming.flatMap(c => [...c.team1, ...c.team2]);
              updatedWaitingList = updatedWaitingList.filter(id => !upcomingPlayerIds.includes(id));
              dlog('🔥 adjustCourtCountHandler - Generated new upcoming match with players:', upcomingPlayerIds);
            } else {
              dlog('🔥 adjustCourtCountHandler - No valid match could be generated');
            }
          } else {
            dlog(
              '🔥 adjustCourtCountHandler - Not enough players for new upcoming match:',
              updatedWaitingList.length,
              `players (need ${playersPerMatch})`
            );
          }
          dlog('🔥 adjustCourtCountHandler - Setting states:');
          dlog('  - updatedCourts:', updatedCourts);
          dlog('  - updatedNextAssignments:', updatedNextAssignments);
          dlog('  - newUpcoming:', newUpcoming);
          dlog('  - final nextAssignments:', [...updatedNextAssignments, ...newUpcoming]);
          
          setCourtAssignments(updatedCourts);
          setNextAssignments([...updatedNextAssignments, ...newUpcoming]);
          setWaitingList(updatedWaitingList);
          // --- END REFACTOR ---
        } else if (finalCount < courtAssignments.length) {
          // SCALABLE APPROACH: Only allow reducing if highest court is empty
          // This maintains sequential numbering and prevents gaps
          let updatedCourts = [...courtAssignments];
          let updatedWaitingList = [...waitingList];
          
          // Sort courts by courtNumber to find highest
          updatedCourts.sort((a, b) => (a.courtNumber || 0) - (b.courtNumber || 0));
          const highestCourt = updatedCourts[updatedCourts.length - 1];
          
          if (!highestCourt) {
            setError('No courts to reduce.');
            return prev;
          }
          
          // Check if highest court is empty (no players)
          const highestCourtIsEmpty = highestCourt.team1.length === 0 && highestCourt.team2.length === 0;
          
          // Check if highest court has in-progress game
          const highestCourtInProgress = highestCourt.inProgress === true;
          
          if (highestCourtInProgress) {
            setError(`Cannot reduce courts. Court ${highestCourt.courtNumber} (highest court) has a game in progress. Finish the game first.`);
            return prev; // Do not change court count
          }
          
          if (!highestCourtIsEmpty) {
            setError(`Cannot reduce courts. Court ${highestCourt.courtNumber} (highest court) must be empty before reducing. Remove players first.`);
            return prev; // Do not change court count
          }
          
          // Calculate how many courts to remove (should be 1 at a time for safety)
          const courtsToRemove = Math.min(courtAssignments.length - finalCount, 1);
          
          // Remove highest court(s) - only if empty
          for (let i = 0; i < courtsToRemove; i++) {
            const courtToRemove = updatedCourts[updatedCourts.length - 1];
            if (courtToRemove && courtToRemove.team1.length === 0 && courtToRemove.team2.length === 0) {
              // Move any players to waiting list (should be none, but safety check)
              const playersToMove = [...courtToRemove.team1, ...courtToRemove.team2];
            updatedWaitingList = [...updatedWaitingList, ...playersToMove];
            
              // Remove highest court
              updatedCourts.pop();
            } else {
              // Should not happen due to check above, but safety net
              setError(`Cannot remove court ${courtToRemove?.courtNumber || 'N/A'} - it is not empty.`);
              return prev;
            }
          }
          
          // Renumber courts sequentially (1, 2, 3, ...) to maintain consistency
          updatedCourts.forEach((court, index) => {
            court.courtNumber = index + 1;
          });
          
          dlog('🔄 Court removal - removed highest court:', highestCourt?.courtNumber);
          dlog('🔄 Court removal - updatedCourts before set:', updatedCourts);
          dlog('🔄 Court removal - updatedCourts.length:', updatedCourts.length);
          dlog('🔄 Court removal - finalCount:', finalCount);
          
          dlog('🔄 About to call setCourtAssignments with:', updatedCourts);
          dlog('🔄 updatedCourts.length:', updatedCourts.length);
          dlog('🔄 Current courtAssignments.length before set:', courtAssignments.length);
          
          setCourtAssignments(updatedCourts);
          setWaitingList(updatedWaitingList);
          
          dlog('🔄 setCourtAssignments called, updatedCourts:', updatedCourts);
          // Recalculate nextAssignments and waitingList
          const allAssignedIds = new Set([
            ...updatedCourts.flatMap(c => [...c.team1, ...c.team2])
          ]);
          const remainingPlayers = activePlayers.filter(p => !allAssignedIds.has(p.id));
          let nextAssignmentsArr = [];
          if (remainingPlayers.length >= playersPerMatch) {
            const maxNextMatches = Math.min(
              numberOfUpcomingCourts,
              Math.floor(remainingPlayers.length / playersPerMatch)
            );
            if (maxNextMatches > 0) {
              const gamesPlayedThisSession = {};
              remainingPlayers.forEach(p => {
                gamesPlayedThisSession[p.id] = p.gamesPlayedThisSession || 0;
              });
              const sessionState = buildSessionState({
                gamesPlayedThisSession,
                courtAssignments: updatedCourts
              });
              const nextResult = callMatchmaking(remainingPlayers.map(p => p.id), maxNextMatches, sessionState, playersById, partnerships, [], gameTypeRestriction, playerCategories);
              nextAssignmentsArr = nextResult.courts;
            }
          }
          const allAssignedIdsFinal = new Set([
            ...updatedCourts.flatMap(c => [...c.team1, ...c.team2]),
            ...nextAssignmentsArr.flatMap(c => [...c.team1, ...c.team2])
          ]);
          // Sort waiting list by effective games played (ascending) to prioritize players with fewer games
          const sortedWaitingList = activePlayers
            .filter(p => !allAssignedIdsFinal.has(p.id))
            .sort((a, b) => {
              const aGames = a.effectiveGamesPlayed !== undefined ? a.effectiveGamesPlayed : (a.gamesPlayedThisSession || 0);
              const bGames = b.effectiveGamesPlayed !== undefined ? b.effectiveGamesPlayed : (b.gamesPlayedThisSession || 0);
              return aGames - bGames;
            });
          setWaitingList(sortedWaitingList);
        } else {
          // If same, do nothing
        }
      }
      return finalCount;
    });
  }, [
    courtsAssigned,
    activePlayers,
    getMaxCourts,
    courtAssignments,
    nextAssignments,
    waitingList,
    playersById,
    sessionPlayerStatus,
    numberOfUpcomingCourts,
    partnerships,
    playersPerMatch,
    callMatchmaking,
    gameTypeRestriction,
    playerCategories,
  ]);

  /**
   * Adjusts the number of waiting courts
   * @param {number} increment - Amount to increment/decrement by
   */
  const adjustWaitingCourtCountHandler = useCallback((increment) => {
    setNumberOfWaitingCourts(prev => adjustWaitingCourtCount(prev, increment));
  }, []);

  // ===== WAITING LIST MANAGEMENT =====

  /**
   * Updates the waiting list with new data
   * @param {Array} newWaitingList - New waiting list to set
   */
  const updateWaitingList = useCallback((newWaitingList) => {
    setWaitingList(newWaitingList);
  }, []);

  /**
   * Generates waiting courts (placeholder implementation)
   * @returns {Object} Success status
   */
  const generateWaitingCourts = useCallback(() => {
    // Implementation for generating waiting courts
    return { success: true };
  }, []);

  const useServerMatchmaking =
    !isSocialSession && !isPegBoardSession && !!sessionId && typeof onAfterFinishMatchSync === 'function';

  // Helper function to fill the playing court (when court is empty)
  const fillPlayingCourt = useCallback((courtNumber, courtIndex, court) => {
    if (useServerMatchmaking) {
      return {
        success: false,
        message: 'Court fill is server-authoritative for this session.',
      };
    }

    const courtIsEmpty = (court.team1 || []).length === 0 && (court.team2 || []).length === 0;

    if (courtIsEmpty) {
      // Empty court: use the canonical single planning path (same as upcoming slots).
      // Scheduled placement is handled server-side; this path is matchmaking-only.

      // Exclude target court from courtAssignments so its players aren't treated as on-court.
      const courtsExcludingTarget = courtAssignments.filter((c) => c.courtNumber !== courtNumber);

      // Next-on-court players are removed from the waitlist when added manually — merge them
      // back into the pool so matchmaking can see them.
      const nextOnCourtPlayers =
        nextAssignments.length > 0
          ? [...(nextAssignments[0].team1 || []), ...(nextAssignments[0].team2 || [])]
          : [];
      const poolWaitingList = [...new Set([...waitingList, ...nextOnCourtPlayers])];

      const pool = buildUpcomingEligiblePool({
        waitingList: poolWaitingList,
        courtAssignments: courtsExcludingTarget,
        nextAssignments,
        playersById,
        sessionPlayerStatus,
        scheduledGames,
        sessionPlayerScheduledGames,
        playerWaitMetrics,
        playersPerMatch,
        matchFormat,
        partnerships,
        cooldownEnabled: cooldownEnabled === true,
      });

      if (pool.eligibleIds.length < playersPerMatch) {
        return {
          success: false,
          message: `Not enough active players for a complete game. Need ${playersPerMatch}, have ${pool.eligibleIds.length}`,
        };
      }

      const poolPlayerIds = [...new Set([...pool.eligibleIds, ...pool.fullPoolIds])];
      const sessionState = buildUpcomingSessionState({
        playersById,
        poolPlayerIds,
        courtAssignments: courtsExcludingTarget,
        gameHistory: Array.isArray(matchHistory) ? matchHistory : null,
        playerInteractions: null,
        totalGamesCompleted: matchHistory?.length ?? null,
        playerWaitMetrics,
        matchHistoryOverride: null,
        matchPreferencesEnabled: matchPreferencesEnabled !== false,
        cooldownEnabled: cooldownEnabled === true,
      });

      const plan = planUpcomingSlot(pool, sessionState, {
        playersById,
        partnerships,
        playerWaitMetrics,
        gameTypeRestriction,
        playerCategories,
        matchFormat,
        playersPerMatch,
        callMatchmakingFn: callMatchmaking,
      });

      if (!plan.type) {
        return { success: false, message: 'No suitable team combination found' };
      }

      const { team1, team2 } = plan.teams;
      const playerIds = [...team1, ...team2];

      const updatedCourts = courtAssignments.map((c) => {
        if (c.courtNumber !== courtNumber) return c;
        const filledCourt = {
          ...c,
          team1,
          team2,
          matchmakingRelaxed: plan.rawCourt?.matchmakingRelaxed || false,
          matchmakingRelaxationDetails: plan.rawCourt?.matchmakingRelaxationDetails || undefined,
          matchmakingDebugInfo: plan.rawCourt?.matchmakingDebugInfo || undefined,
          gameCreation: createMatchmakingMeta(plan.gameCreation),
        };
        markCourtMatchStarted(filledCourt);
        return filledCourt;
      });

      const updatedWaitingList = waitingList.filter((id) => !playerIds.includes(id));
      const onCourtSet = new Set(playerIds);
      const nextAfterFill = (() => {
        if (!nextAssignments?.length) return nextAssignments;
        const first = nextAssignments[0];
        const t1 = (first.team1 || []).filter((id) => !onCourtSet.has(id));
        const t2 = (first.team2 || []).filter((id) => !onCourtSet.has(id));
        const rest = nextAssignments.slice(1);
        if (t1.length === 0 && t2.length === 0) return rest;
        return [{ ...first, team1: t1, team2: t2 }, ...rest];
      })();

      const allPlayersInState = [
        ...updatedCourts.flatMap((c) => [...(c.team1 || []), ...(c.team2 || [])]),
        ...updatedWaitingList,
        ...(nextAfterFill || []).flatMap((m) => [...(m.team1 || []), ...(m.team2 || [])]),
      ];
      const uniquePlayersInState = [...new Set(allPlayersInState)];
      if (allPlayersInState.length !== uniquePlayersInState.length) {
        console.error('🚨 VALIDATION ERROR: Duplicate players detected before state update!', {
          totalPlayers: allPlayersInState.length,
          uniquePlayers: uniquePlayersInState.length,
          duplicates: allPlayersInState.length - uniquePlayersInState.length,
        });
        return {
          success: false,
          message: `Validation failed: ${allPlayersInState.length - uniquePlayersInState.length} duplicate players detected.`,
        };
      }

      setCourtAssignments(updatedCourts);
      setWaitingList(updatedWaitingList);
      setNextAssignments(nextAfterFill);

      if (plan.type === 'scheduled' && typeof onConsumeScheduledGames === 'function') {
        void Promise.resolve(
          onConsumeScheduledGames([String(plan.gameCreation.scheduledGameId)]),
        ).catch((err) => {
          console.error(
            '[useCourtManagement] onConsumeScheduledGames (fillPlayingCourt scheduled):',
            err,
          );
        });
      }

      if (setPlayerSessionStatus) {
        playerIds.forEach((playerId) => {
          setPlayerSessionStatus(playerId, 'active').catch((err) => {
            console.error(`Failed to activate player ${playerId} on court:`, err);
          });
        });
      }

      return {
        success: true,
        message:
          plan.type === 'scheduled'
            ? `Court ${courtNumber} filled with scheduled game`
            : `Court ${courtNumber} filled successfully with optimal teams`,
        matchmakingResult: { courts: [{ team1, team2 }] },
        persistHint: {
          courtNumber,
          team1,
          team2,
          inProgress: true,
          gameCreation: createMatchmakingMeta(plan.gameCreation),
          nextAssignments: nextAfterFill,
          nextAssignmentsChanged: nextAfterFill !== nextAssignments,
        },
      };
    }

    // Partial court fill: court already has some players — merge matchmaking result in.
    // Scheduled game promotion is not applicable here (would conflict with existing players).
    const activePlayersForMatchmaking = [...waitingList].filter(
      (id) => playersById[id] && sessionPlayerStatus[id] === 'active',
    );

    if (activePlayersForMatchmaking.length < playersPerMatch) {
      return {
        success: false,
        message: `Not enough active players for a complete game. Need ${playersPerMatch}, have ${activePlayersForMatchmaking.length}`,
      };
    }

    const gamesPlayedThisSession = {};
    activePlayersForMatchmaking.forEach((id) => {
      const p = playersById[id];
      if (p) gamesPlayedThisSession[id] = p.gamesPlayedThisSession || 0;
    });

    const recentTeams = new Set();
    const recentMatchups = new Set();
    courtAssignments.forEach((c) => {
      if (c.team1.length === playersPerSide) recentTeams.add(c.team1.sort().join('-'));
      if (c.team2.length === playersPerSide) recentTeams.add(c.team2.sort().join('-'));
      if (c.team1.length === playersPerSide && c.team2.length === playersPerSide) {
        recentMatchups.add([...c.team1, ...c.team2].sort().join('-'));
      }
    });

    const sessionState = buildSessionState({
      gamesPlayedThisSession,
      courtAssignments,
      recentTeams,
      recentMatchups,
    });

    // Engine gets [] for scheduledGames — scheduled players already excluded from pool above.
    const result = callMatchmaking(
      activePlayersForMatchmaking,
      1,
      sessionState,
      playersById,
      partnerships,
      [],
      gameTypeRestriction,
      playerCategories,
    );

    if (result.courts.length === 0) {
      return { success: false, message: 'No suitable team combination found' };
    }

    const bestCourt = result.courts[0];
    const existingTeam1 = [...court.team1];
    const existingTeam2 = [...court.team2];

    const newTeam1 = [...existingTeam1];
    const playersAddedToTeam1 = [];
    bestCourt.team1.forEach((playerId) => {
      if (!newTeam1.includes(playerId) && newTeam1.length < playersPerSide) {
        newTeam1.push(playerId);
        playersAddedToTeam1.push(playerId);
      }
    });

    const newTeam2 = [...existingTeam2];
    const playersAddedToTeam2 = [];
    bestCourt.team2.forEach((playerId) => {
      if (!newTeam2.includes(playerId) && newTeam2.length < playersPerSide) {
        newTeam2.push(playerId);
        playersAddedToTeam2.push(playerId);
      }
    });

    const actuallyAddedPlayerIds = [...playersAddedToTeam1, ...playersAddedToTeam2];
    const updatedWaitingList = waitingList.filter((id) => !actuallyAddedPlayerIds.includes(id));

    const isFull = newTeam1.length === playersPerSide && newTeam2.length === playersPerSide;
    const gameCreationMeta = createMatchmakingMeta({
      origin: GAME_CREATION_ORIGIN.MATCHMAKING,
      algorithm: MATCHMAKING_ALGORITHM_NAME,
      scheduledGameId: null,
      isAltered: true,
    });

    const updatedCourts = [...courtAssignments];
    const refilledCourt = {
      ...court,
      team1: newTeam1,
      team2: newTeam2,
      matchmakingRelaxed: bestCourt.matchmakingRelaxed || false,
      matchmakingRelaxationDetails: bestCourt.matchmakingRelaxationDetails || undefined,
      matchmakingDebugInfo: bestCourt.matchmakingDebugInfo || undefined,
      gameCreation: gameCreationMeta,
    };
    if (isFull) {
      markCourtMatchStarted(refilledCourt);
    } else {
      refilledCourt.inProgress = false;
    }
    updatedCourts[courtIndex] = refilledCourt;

    const onCourtIds = new Set([...newTeam1, ...newTeam2]);
    const nextAfterFill = (() => {
      if (!nextAssignments?.length) return nextAssignments;
      const first = nextAssignments[0];
      const t1 = (first.team1 || []).filter((id) => !onCourtIds.has(id));
      const t2 = (first.team2 || []).filter((id) => !onCourtIds.has(id));
      const rest = nextAssignments.slice(1);
      if (t1.length === 0 && t2.length === 0) return rest;
      return [{ ...first, team1: t1, team2: t2 }, ...rest];
    })();

    const allPlayersInState = [
      ...updatedCourts.flatMap((c) => [...(c.team1 || []), ...(c.team2 || [])]),
      ...updatedWaitingList,
      ...(nextAfterFill || []).flatMap((m) => [...(m.team1 || []), ...(m.team2 || [])]),
    ];
    const uniquePlayersInState = [...new Set(allPlayersInState)];
    if (allPlayersInState.length !== uniquePlayersInState.length) {
      console.error('🚨 VALIDATION ERROR: Duplicate players detected before state update!', {
        totalPlayers: allPlayersInState.length,
        uniquePlayers: uniquePlayersInState.length,
        duplicates: allPlayersInState.length - uniquePlayersInState.length,
      });
      return {
        success: false,
        message: `Validation failed: ${allPlayersInState.length - uniquePlayersInState.length} duplicate players detected.`,
      };
    }

    setCourtAssignments(updatedCourts);
    setWaitingList(updatedWaitingList);
    setNextAssignments(nextAfterFill);

    if (actuallyAddedPlayerIds.length > 0 && setPlayerSessionStatus) {
      actuallyAddedPlayerIds.forEach((playerId) => {
        setPlayerSessionStatus(playerId, 'active').catch((err) => {
          console.error(`Failed to activate player ${playerId} on court:`, err);
        });
      });
    }

    return {
      success: true,
      message: `Court ${courtNumber} filled successfully with optimal teams`,
      matchmakingResult: result,
    };
  }, [
    courtAssignments,
    waitingList,
    setCourtAssignments,
    setWaitingList,
    setNextAssignments,
    playersById,
    partnerships,
    sessionPlayerStatus,
    nextAssignments,
    setPlayerSessionStatus,
    scheduledGames,
    sessionPlayerScheduledGames,
    playerWaitMetrics,
    playersPerMatch,
    playersPerSide,
    matchFormat,
    matchHistory,
    matchPreferencesEnabled,
    cooldownEnabled,
    callMatchmaking,
    gameTypeRestriction,
    playerCategories,
    onConsumeScheduledGames,
    buildSessionState,
    useServerMatchmaking,
  ]);
  
  // Helper function to fill next-on-court (when playing court is active)
  const fillNextOnCourt = useCallback((courtNumber, courtIndex, court, excludedMatches = new Set()) => {
    if (useServerMatchmaking) {
      return {
        success: false,
        message: 'Upcoming court fill is server-authoritative for this session.',
      };
    }

    // Manual upcoming lineups are not on the waitlist — merge them into the pool so matchmaking
    // can see them and we return anyone not selected back to the waitlist (no ghost players).
    const firstNext = nextAssignments[0];
    const existingOnNext = firstNext
      ? [...(firstNext.team1 || []), ...(firstNext.team2 || [])]
      : [];
    const poolIds = [...new Set([...waitingList, ...existingOnNext])];

    const activePlayersForMatchmaking = poolIds.filter(
      id =>
        playersById[id] &&
        sessionPlayerStatus[id] === 'active'
    );

    if (activePlayersForMatchmaking.length < playersPerMatch) {
      return {
        success: false,
        message: `Not enough players for next-on-court. Need ${playersPerMatch}, have ${activePlayersForMatchmaking.length}`,
      };
    }
    
    // Use smart matchmaking
    const gamesPlayedThisSession = {};
    activePlayersForMatchmaking.forEach(id => {
      const p = playersById[id];
      if (p) gamesPlayedThisSession[id] = p.gamesPlayedThisSession || 0;
    });
    
    const sessionState = buildSessionState({
      gamesPlayedThisSession,
      courtAssignments,
      playerInteractions: new Map(), // TODO: Get from Dashboard state
      playerStreaks: {} // TODO: Get from Dashboard state
    });
    
    // Use matchmaking based on selected mode
    // Note: excludedMatches only supported in standard mode
    const result = callMatchmaking(activePlayersForMatchmaking, 1, sessionState, playersById, partnerships, [], gameTypeRestriction, playerCategories);
    
    if (!result || result.courts.length === 0) {
      return { success: false, message: 'No suitable team combination found. Try removing some players and adding them back.' };
    }
    
    const bestMatch = result.courts[0];
    
    const nextMatchMeta = createMatchmakingMeta({
      origin: GAME_CREATION_ORIGIN.MATCHMAKING,
      algorithm: MATCHMAKING_ALGORITHM_NAME,
      scheduledGameId: null,
      isAltered: false,
    });

    const selectedIds = new Set([...bestMatch.team1, ...bestMatch.team2]);
    let updatedWaitingList = waitingList.filter(id => !selectedIds.has(id));
    existingOnNext.forEach(id => {
      if (!selectedIds.has(id) && !updatedWaitingList.includes(id)) {
        updatedWaitingList.push(id);
      }
    });

    const updatedNextAssignments = [
      {
        team1: bestMatch.team1,
        team2: bestMatch.team2,
        inProgress: false,
        winner: null,
        score: null,
        gameCreation: nextMatchMeta,
        ...(firstNext?.courtNumber != null ? { courtNumber: firstNext.courtNumber } : {}),
      },
      ...nextAssignments.slice(1),
    ];

    setNextAssignments(updatedNextAssignments);
    setWaitingList(updatedWaitingList);
    
    dlog(`✅ [FILL_NEXT] Successfully filled next-on-court for court ${courtNumber}`);
    return { success: true, message: `Next-on-court filled successfully`, match: bestMatch };
  }, [
    courtAssignments,
    waitingList,
    nextAssignments,
    playersById,
    sessionPlayerStatus,
    partnerships,
    setNextAssignments,
    setWaitingList,
    playersPerMatch,
    callMatchmaking,
    gameTypeRestriction,
    playerCategories,
    useServerMatchmaking,
  ]);

  /**
   * Fills an empty court with players from the waiting list using smart matchmaking
   * Delegates to fillPlayingCourt or fillNextOnCourt based on court state
   * @param {number} courtNumber - The court number to fill
   * @param {Set} excludedMatches - Set of match signatures to exclude from generation
   * @returns {Object} Success status and message
   */
  const fillEmptyCourt = useCallback((courtNumber, excludedMatches = new Set()) => {
    
    // Find the court
    const courtIndex = courtAssignments.findIndex(court => court.courtNumber === courtNumber);
    if (courtIndex === -1) {
      return { success: false, message: 'Court not found' };
    }

    const court = courtAssignments[courtIndex];
    const isPlayingCourtEmpty = court.team1.length === 0 && court.team2.length === 0;
    const isPlayingCourtActive = !isPlayingCourtEmpty && court.inProgress;
    
    
    // Determine what to fill
    if (isPlayingCourtEmpty) {
      return fillPlayingCourt(courtNumber, courtIndex, court);
    } else if (isPlayingCourtActive) {
      return fillNextOnCourt(courtNumber, courtIndex, court, excludedMatches);
    } else {
      return { success: false, message: 'Court is in invalid state' };
    }
  }, [courtAssignments, fillPlayingCourt, fillNextOnCourt]);

  // ===== EFFECTS =====

  useEffect(() => {
    fetchClubStrengths();
  }, []); // Remove fetchClubStrengths dependency to prevent re-creation

  // Note: Removed automatic court count adjustment to respect database values
  // Court count should be set from session data, not automatically adjusted

  // Update partnerships in service when they change
  useEffect(() => {
    const updatePartnershipData = async () => {
      try {
        await updatePartnerships(partnerships);
      } catch (err) {
        console.error('Error updating partnerships:', err);
      }
    };
    
    updatePartnershipData();
  }, [partnerships?.length]); // Only depend on partnerships length to prevent excessive re-runs

  // REMOVED: Debug useEffect for courtAssignments changes - was causing unnecessary re-renders
  // This was only for debugging and was running on every courtAssignments change

  // Optionally, provide a way to reset userSetNumberOfCourts (e.g., on session reset)
  const resetUserSetNumberOfCourts = () => setUserSetNumberOfCourts(false);

  // ===== RETURN OBJECT =====

  // Wrapper to track if user manually set number of courts
  const setNumberOfCourts = (val) => {
    setNumberOfCourtsState(val);
    setUserSetNumberOfCourts(true);
  };

  return {
    // State
    courtAssignments,
    nextAssignments,
    waitingList,
    pausedList,
    numberOfCourts: numberOfCourtsState, // Expose the state value
    userSetNumberOfCourts,
    numberOfWaitingCourts,
    numberOfUpcomingCourts,
    currentClubId,
    playerWaitMetrics, // Export wait metrics for Dashboard access
    setPlayerWaitMetrics, // Export setter for Dashboard to restore state from DB
    clubStrengths,
    loading,
    error,
    courtsAssigned, // Add courtsAssigned to the return object
    gameHistory, // Add gameHistory to the return object
    
    // State setters (for advanced use cases)
    setCourtAssignments,
    setNextAssignments,
    setWaitingList,
    setPausedList,
    setNumberOfUpcomingCourts,
    setGameHistory, // Add setGameHistory to the return object
    setNumberOfCourts, // <-- Only export the wrapper
    resetUserSetNumberOfCourts, // <-- Export reset function
    
    // Court operations
    assignCourts,
    updateMatchResult: updateMatchResultHandler,
    removeCourt: removeCourtHandler,
    removePlayerFromCourt: removePlayerFromCourtHandler,
    rotateAndRefillCourts,

    // Court count management
    adjustCourtCount: adjustCourtCountHandler,
    adjustWaitingCourtCount: adjustWaitingCourtCountHandler,
    getMaxCourts,
    canReduceCourts,
    reduceCourtsReason,
    
    // Waiting list management
    updateWaitingList,
    generateWaitingCourts,
    fillEmptyCourt,

    matchHistory,
    reloadMatchHistory,
    serverClearedActiveCourtsRef,
    serverClearedCourtRosterSigRef,
    /** When true with a persisted session, score submission triggers client rotation — hide redundant manual Rotate. */
    autoRotateAfterScoreSubmission,

    // Club management
    setClub,
    fetchClubStrengths
  };
};

export default useCourtManagement; 